import { create } from "zustand";
import {
  devtools,
  persist,
  createJSONStorage,
  type PersistStorage,
} from "zustand/middleware";
import { isBefore, startOfDay, format, isSameDay } from "date-fns";
import { de, enUS, fr, nl } from "date-fns/locale";
import { toast } from "sonner";
import i18next from "i18next";
import { applyMarketEnglish, languageMap } from "@/i18n";
import type {
  AnalysisStep,
  BookingStep,
  TimeSlot,
  BookingLanguage,
} from "@/types/booking";
import type { Product } from "@/components/product-step/types";
import type { Clinic } from "@/hooks/useClinics";
import { dropUnavailableCartItems } from "@/utils/dropUnavailableCartItems";
import {
  type MarketCountry,
  DEFAULT_MARKET,
  marketUrlParams,
  parseMarketFromUrl,
  parseForcedPackageTypes,
  coerceLanguageForMarket,
} from "@/config/market";
import posthog from "posthog-js";
import {
  getFirstAvailableDate,
  getNextAvailableDate,
  type ExternalSlot,
} from "@/utils/dateUtils";
import {
  getProductIdsFromUrl,
  updateUrlWithBookingParams,
  getTestModeParams,
  pushStepToHistory,
  replaceStepInUrl,
  getStepFromUrl,
  getUrlParam,
} from "@/utils/urlUtils";
import { refreshOnTabFocus } from "@/stores/tabFocusRefresh";
import {
  isAnalyticsSuppressed,
  stripPiiForPostHog,
  trackEvent,
} from "@/utils/analytics";
import { entrySourceUrlProducts } from "@/utils/entrySource";
import { cartEntryStep, isOneTimeHandoff } from "@/utils/cartEntryStep";
import { scrollToTopDelayed } from "@/utils/scrollUtils";
import { safeLocalStorage } from "@/utils/safeStorage";
import { getUnavailableProductsWithReasons } from "@/hooks/useClinicProductAvailability";
import { getBookableTimesForDate } from "@/utils/bookableSlots";
import { supabase } from "@/integrations/supabase/client";
import {
  BOOKING_STEP_REQUIREMENTS,
  STANDARD_STEP_ORDER,
  MEMBERSHIP_STEP_ORDER,
} from "./bookingFlowModel";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { pickBaselineProduct } from "@/components/product-step/pickBaselineProduct";
import type { RecommendedPlan } from "@/utils/membershipPricing";
import { sessionStateService } from "@/services/sessionStateService";
import { useBookingStore } from "@/stores/bookingStore";
import { captureUrlParams, getFullUrl } from "@/utils/urlParamsCapture";
import type {
  SessionStateSnapshot,
  SessionFormData,
} from "@/types/sessionState";

// ============================================================================
// MEMOIZATION CACHE for expensive computed getters
// These functions are called multiple times per render cycle from useBookingFlow.
// Cache keyed on store state references (which don't change within a render batch).
// ============================================================================

// Cache for getFirstAvailableDate - keyed on the 5 store fields it depends on
let _fadCache: {
  keys: [string | null, string[], string[], Clinic[], Product[]];
  result: Date;
} | null = null;

function cachedGetFirstAvailableDate(
  selectedClinic: string | null,
  selectedProducts: string[],
  selectedAddOns: string[],
  clinics: Clinic[],
  products: Product[],
): Date {
  if (
    _fadCache &&
    _fadCache.keys[0] === selectedClinic &&
    _fadCache.keys[1] === selectedProducts &&
    _fadCache.keys[2] === selectedAddOns &&
    _fadCache.keys[3] === clinics &&
    _fadCache.keys[4] === products
  ) {
    return _fadCache.result;
  }

  const clinic = clinics.find((c) => c.id === selectedClinic);
  const selectedProductDetails = products.filter((p) =>
    selectedProducts.includes(p.id),
  );
  const selectedAddOnDetails = products.filter((p) =>
    selectedAddOns.includes(p.id),
  );

  const result = getFirstAvailableDate(
    clinic || null,
    selectedProductDetails,
    selectedAddOnDetails,
  );

  _fadCache = {
    keys: [selectedClinic, selectedProducts, selectedAddOns, clinics, products],
    result,
  };

  return result;
}

// Cache for getDefaultSecondSlotDate - keyed on the 6 store fields it depends on
let _dsdCache: {
  keys: [TimeSlot[], string | null, string[], string[], Clinic[], Product[]];
  result: Date | undefined;
} | null = null;

function cachedGetDefaultSecondSlotDate(
  selectedTimeSlots: TimeSlot[],
  selectedClinic: string | null,
  selectedProducts: string[],
  selectedAddOns: string[],
  clinics: Clinic[],
  products: Product[],
): Date | undefined {
  if (
    _dsdCache &&
    _dsdCache.keys[0] === selectedTimeSlots &&
    _dsdCache.keys[1] === selectedClinic &&
    _dsdCache.keys[2] === selectedProducts &&
    _dsdCache.keys[3] === selectedAddOns &&
    _dsdCache.keys[4] === clinics &&
    _dsdCache.keys[5] === products
  ) {
    return _dsdCache.result;
  }

  const firstSlot = selectedTimeSlots[0];
  if (!firstSlot?.date) {
    _dsdCache = {
      keys: [
        selectedTimeSlots,
        selectedClinic,
        selectedProducts,
        selectedAddOns,
        clinics,
        products,
      ],
      result: undefined,
    };
    return undefined;
  }

  const nextDay = new Date(firstSlot.date);
  nextDay.setDate(nextDay.getDate() + 1);

  const clinic = clinics.find((c) => c.id === selectedClinic);
  const selectedProductDetails = products.filter((p) =>
    selectedProducts.includes(p.id),
  );
  const selectedAddOnDetails = products.filter((p) =>
    selectedAddOns.includes(p.id),
  );

  const result = getNextAvailableDate(
    nextDay,
    clinic || null,
    selectedProductDetails,
    selectedAddOnDetails,
  );

  _dsdCache = {
    keys: [
      selectedTimeSlots,
      selectedClinic,
      selectedProducts,
      selectedAddOns,
      clinics,
      products,
    ],
    result,
  };

  return result;
}

// Cache for getSelectedProductDetails - returns stable array reference
let _spdCache: {
  keys: [string[], Product[]];
  result: Product[];
} | null = null;

function cachedGetSelectedProductDetails(
  selectedProducts: string[],
  products: Product[],
): Product[] {
  if (
    _spdCache &&
    _spdCache.keys[0] === selectedProducts &&
    _spdCache.keys[1] === products
  ) {
    return _spdCache.result;
  }

  const result = products.filter((p) => selectedProducts.includes(p.id));
  _spdCache = { keys: [selectedProducts, products], result };
  return result;
}

// Cache for getSelectedAddOnDetails - returns stable array reference
let _sadCache: {
  keys: [string[], Product[]];
  result: Product[];
} | null = null;

function cachedGetSelectedAddOnDetails(
  selectedAddOns: string[],
  products: Product[],
): Product[] {
  if (
    _sadCache &&
    _sadCache.keys[0] === selectedAddOns &&
    _sadCache.keys[1] === products
  ) {
    return _sadCache.result;
  }

  const result = products.filter((p) => selectedAddOns.includes(p.id));
  _sadCache = { keys: [selectedAddOns, products], result };
  return result;
}

// Cache for getSelectedClinicDetails - returns stable reference
let _scdCache: {
  keys: [string | null, Clinic[]];
  result: Clinic | undefined;
} | null = null;

function cachedGetSelectedClinicDetails(
  selectedClinic: string | null,
  clinics: Clinic[],
): Clinic | undefined {
  if (
    _scdCache &&
    _scdCache.keys[0] === selectedClinic &&
    _scdCache.keys[1] === clinics
  ) {
    return _scdCache.result;
  }

  const result = clinics.find((c) => c.id === selectedClinic);
  _scdCache = { keys: [selectedClinic, clinics], result };
  return result;
}

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  canProceed: boolean;
}

interface ToastMessage {
  message: string;
  type: "info" | "warning" | "error" | "success";
  duration?: number;
  position?:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "top-center"
    | "bottom-center";
}

interface SlotSelectionOptions {
  previousSlot?: TimeSlot;
  preference: "first" | "second";
  dateOverride?: Date;
  isFromExternalSlot?: boolean; // true if slot comes from external system, false if from opening_hours/dialog
}

/**
 * Snapshot of the submitted booking payload, kept only so the confirmation
 * page can render "check your inbox" details after the form unmounts. The
 * store never reads a field off it — it stashes whatever `submitBooking()`
 * was handed (the snake_case payload from `buildBookingPayload()` plus the
 * `discount` / `total_price` BookingForm adds, or the untyped snapshot the
 * embedded-payment path replays). `ConfirmationStep` declares the subset it
 * actually reads.
 *
 * ponytail: deliberately loose. This used to claim a required camelCase
 * `firstName`/`lastName`/`email`/`phone` shape that nothing ever wrote, which
 * only produced type errors at the two real call sites. Tighten it by giving
 * `buildBookingPayload()` an exported return type, not by guessing here.
 */
type FormData = Record<string, unknown>;

// ============================================================================
// STATE INTERFACE
// ============================================================================

interface BookingFlowState {
  // ===== STEP MANAGEMENT =====
  currentStep: BookingStep;
  stepHistory: BookingStep[];

  // ===== PRODUCT SELECTION =====
  selectedProducts: string[];
  selectedAddOns: string[];
  selectedPackageId: string | null;
  /** Blood package the user had before attaching a scan — restored on detach. */
  bloodBaselineBeforeScanId: string | null;
  selectedPackageType: string;
  viewMode: "packages" | "configure";
  configureProducts: string[];

  // ===== CLINIC SELECTION =====
  selectedClinic: string | null;

  // ===== DATE & TIME SELECTION =====
  selectedDate: Date | null;
  wasDateManuallySelected: boolean; // Track if user explicitly selected the date (vs auto-selected)
  selectedTimeSlots: TimeSlot[];
  secondSlotCalendarDate: Date | null;
  bookingType: "request";

  // ===== BOOKING MODE (external slots vs request) =====
  bookingMode: "external" | "request";

  // ===== PAYMENT MODE (blood test experiment) =====
  // null = no explicit choice yet (payment variant 4 starts unselected)
  selectedPaymentMode: "yearly" | "once" | null;
  // Set true when the user tries to proceed without picking a payment option,
  // so the option cards can flag themselves red. Cleared once a mode is chosen.
  paymentModeError: boolean;
  // Which membership path opened the Stripe checkout: cadence flow or the
  // details-page upsell (AHD-7022). Snapshotted into booking_step so the
  // confirmation event classifies the purchase as a membership — the ONLY
  // basket-level membership marker for upsell purchases. Set (and synced)
  // right before the payment modal opens; cleared when the user backs out to
  // a non-membership mode.
  membershipCheckoutInitiated: "cadence" | "upsell" | null;

  // ===== FORM & SUBMISSION =====
  formData: FormData | null;
  bookingId: string | null;
  sessionId: string | null;
  isSubmitting: boolean; // true during active submission
  isSubmitted: boolean; // sticky: true after successful submission

  // ===== UI STATE =====
  isLoading: boolean;
  language: BookingLanguage;

  // ===== MARKET (country) =====
  // Drives currency, clinic filtering, phone/address defaults, package-type
  // pills, consent boxes and payment logos. Defaults to the clinics.country
  // default ('ch'). Switching market restarts the booking flow.
  market: MarketCountry;

  // Package types force-shown via `?force=mri`, overriding the market's hidden
  // set so an MRI landing page can still reach the MRI pill (AHD-7129).
  forcedPackageTypes: readonly string[];

  // ===== INITIALIZATION =====
  hasInitializedFromUrl: boolean;

  /** Flow marker stored on every session state entry: "membership-first" in
   *  that app (set once at its boot), undefined for the booking flow. */
  sessionFlow: "membership-first" | undefined;

  // ===== SESSION CONFLICT =====
  pendingSessionConflict: boolean;
  _conflictSessionProducts: string[];
  _conflictSessionAddOns: string[];
  _conflictUrlProducts: string[];

  // ===== COMPUTED STATE DEPENDENCIES =====
  // These are passed from outside (products, clinics) for calculations
  _products: Product[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _clinics: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _clinicProductAvailability: any[];
  _clinicExternalSlots: Record<string, ExternalSlot[]>;

  // ===== INTERNAL FLAGS =====
  _skipNextSync: boolean;
  _pendingEmptyUrlApply: boolean;

  // ===== ACTIONS: INITIALIZATION =====
  setProducts: (products: Product[]) => void;
  setClinics: (clinics: Clinic[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setClinicProductAvailability: (availability: any[]) => void;
  setClinicExternalSlots: (clinicId: string, slots: ExternalSlot[]) => void;
  setLanguage: (language: BookingLanguage) => void;
  setMarket: (market: MarketCountry) => void;
  initializeFromUrl: (
    products?: Product[],
    isBuildYourOwnEnabled?: boolean,
  ) => void;
  initializeTestModeNavigation: () => void;

  // ===== ACTIONS: STEP NAVIGATION =====
  goToStep: (
    step: BookingStep,
    options?: {
      skipValidation?: boolean;
      skipScroll?: boolean;
      skipHistory?: boolean;
    },
  ) => void;
  goBack: () => void;
  canGoBack: () => boolean;
  setStepFromHistory: (step: BookingStep) => void;

  // ===== ACTIONS: PAYMENT MODE =====
  setSelectedPaymentMode: (mode: "yearly" | "once" | null) => void;
  setPaymentModeError: (hasError: boolean) => void;
  markMembershipCheckoutInitiated: (source: "cadence" | "upsell") => void;

  // ===== ACTIONS: PRODUCT SELECTION =====
  setViewMode: (mode: "packages" | "configure") => void;
  setSelectedPackageType: (packageType: string) => void;
  attachScanToMembership: (packageId: string) => void;
  detachScanFromMembership: () => void;
  /** Remove the membership from the cart, keeping any scan. Clears the plan +
   *  bundle + membership-first flags and payment mode, then persists so a session
   *  reload / back-nav can't silently restore it. */
  detachMembership: () => void;
  selectProducts: (
    products: string[],
    options?: { skipNavigation?: boolean; skipAddons?: boolean },
  ) => void;
  addProduct: (productId: string) => void;
  removeProduct: (productId: string) => void;
  setSelectedPackageId: (packageId: string | null) => void;
  setConfigureProducts: (products: string[]) => void;

  // ===== ACTIONS: ADD-ON SELECTION =====
  toggleAddOn: (addOnId: string) => void;
  setSelectedAddOns: (addOns: string[]) => void;

  // ===== ACTIONS: CLINIC SELECTION =====
  selectClinic: (clinicId: string) => void;

  // ===== ACTIONS: DATE & TIME SELECTION =====
  selectDate: (date: Date | null, options?: { isManual?: boolean }) => void;
  selectTimeSlot: (times: string[], options: SlotSelectionOptions) => void;
  deleteTimeSlot: (slot: TimeSlot) => void;
  setSecondSlotCalendarDate: (date: Date | null) => void;
  setBookingType: (type: "request") => void;

  // ===== ACTIONS: BOOKING MODE =====
  setBookingMode: (mode: "external" | "request") => void;

  // ===== ACTIONS: FORM SUBMISSION =====
  setSubmitting: (value: boolean) => void;
  setSubmitted: (value: boolean) => void;
  updateFormData: (data: FormData) => void;
  submitBooking: (data: FormData) => Promise<void>;
  setBookingId: (bookingId: string) => void;
  /** Return the booking id, generating + persisting one if absent. The stable
   *  idempotency anchor shared by the browser submit and the Stripe webhook. */
  getOrCreateBookingId: () => string;
  clearFormData: () => void;
  setSessionId: (id: string | null) => void;
  createStateSnapshot: () => SessionStateSnapshot;
  syncSession: (reason?: string) => Promise<void>;
  /** Record a step this store does not drive (the membership-first app's
   *  entry, plan and payment) in session state, with none of syncSession's
   *  restore guards: those protect this store's own server-restored state. */
  recordStep: (
    step: AnalysisStep,
    reason: string,
    extra?: Partial<SessionStateSnapshot>,
  ) => Promise<void>;
  loadSession: (id: string) => Promise<void>;
  refreshFromServer: () => Promise<void>;

  // ===== ACTIONS: VALIDATION =====
  validateStep: (step: BookingStep) => ValidationResult;
  canProceedFromStep: (step: BookingStep) => boolean;
  _recalculateAndValidateDate: () => void;
  _validateTimeSlotsForClinic: () => void;
  /** The picked times are paid for (membership-first, booked as picked):
   *  _validateTimeSlotsForClinic leaves them alone. Not persisted. */
  pickedTimesPaid: boolean;

  // ===== ACTIONS: TOAST MANAGEMENT =====
  showToast: (toast: ToastMessage) => void;

  // ===== ACTIONS: PERSISTED DATE VALIDATION =====
  _validatePersistedDate: () => void;

  // ===== ACTIONS: SESSION CONFLICT =====
  resolveSessionConflict: (choice: "session" | "url" | "startOver") => void;

  // ===== ACTIONS: URL CART ENTRY =====
  /** After a `?products=` cart is applied: open the step its link asked for
   *  (cartEntryStep), the steps before it as history, when the cart lets that
   *  step be entered. Without one, the step stays as it was. */
  _openCartEntryStep: (hasPackage: boolean) => void;
  /** On the membership-first one-off handoff (isOneTimeHandoff) only: drop
   *  the membership a restored session holds. A no-op on any other link. */
  _dropMembershipOnOneTimeHandoff: () => void;

  // ===== ACTIONS: RESET =====
  resetFlow: () => void;
  resetFromStep: (step: BookingStep) => void;
  clearCart: () => void;

  // ===== SELECTORS (Computed State) =====
  getFirstAvailableDate: () => Date;
  getDefaultSecondSlotDate: () => Date | undefined;
  isDateValid: () => boolean;
  getSelectedProductDetails: () => Product[];
  getSelectedAddOnDetails: () => Product[];
  getSelectedClinicDetails: () => Clinic | undefined;
  getInferredPackageType: () => string;
  getAvailablePackageTypes: () => string[];
  getMissingRequirements: (step: BookingStep) => string[];
  ensureStepValidity: (step: BookingStep) => BookingStep;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  pickedTimesPaid: false,
  currentStep: "product" as BookingStep,
  stepHistory: [] as BookingStep[],
  viewMode: "packages" as const,
  selectedPackageType: "mri" as const,
  selectedProducts: [] as string[],
  selectedAddOns: [] as string[],
  configureProducts: [] as string[],
  selectedPackageId: null,
  bloodBaselineBeforeScanId: null,
  selectedClinic: null,
  selectedDate: null,
  wasDateManuallySelected: false,
  selectedTimeSlots: [] as TimeSlot[],
  secondSlotCalendarDate: null,
  bookingType: "request" as const,
  bookingMode: "external" as const,
  // COMPLIANCE: nothing pre-selected — a membership must never be pre-ticked. A
  // scan cart only becomes a bundle once the user opts in (the add-ons "Add
  // membership" card sets membershipPlanStore.active, or path-B picks "yearly" at
  // the details step). Defaulting to "yearly" showed — and would have charged — a
  // membership on the add-ons step before any opt-in. FormFields enforces the same
  // "start empty" rule on the details step; this makes it true from the start.
  selectedPaymentMode: null,
  paymentModeError: false,
  membershipCheckoutInitiated: null as "cadence" | "upsell" | null,
  formData: null,
  bookingId: null,
  isSubmitting: false,
  isSubmitted: false,
  isLoading: false,
  language: "en-US" as BookingLanguage,
  market: DEFAULT_MARKET as MarketCountry,
  forcedPackageTypes: [] as readonly string[],
  hasInitializedFromUrl: false,
  sessionFlow: undefined,
  pendingSessionConflict: false,
  _conflictSessionProducts: [] as string[],
  _conflictSessionAddOns: [] as string[],
  _conflictUrlProducts: [] as string[],
  _products: [] as Product[],
  _clinics: [] as Clinic[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _clinicProductAvailability: [] as any[],
  _clinicExternalSlots: {} as Record<string, ExternalSlot[]>,
  sessionId: null,
  _skipNextSync: false,
  _pendingEmptyUrlApply: false,
};

// ============================================================================
// HELPERS
// ============================================================================

const CART_STORAGE_KEY = "booking-cart";
const SESSION_COOKIE_NAME = "booking_session_id";

// The active linear step order depends on the ENTRY DIRECTION, not merely on
// whether a membership is active:
//   • Membership-first (blood tab → "Become a member") visits the extra
//     `cadence` step → MEMBERSHIP_STEP_ORDER (6 steps).
//   • MRI-first + bundle (scan selected, membership attached on the add-ons
//     step) never visits those two → it stays the STANDARD 5-step funnel even
//     though `active` is true.
// Keying on `membershipFirst` (not `active`) keeps back / history / validity
// navigation — and the progress indicator — matching the path the user is
// actually on. Session-restore code derives the order from the persisted
// `membershipFirst` flag instead (the store isn't restored yet at that point).
function getActiveStepOrder(): BookingStep[] {
  return useMembershipPlanStore.getState().membershipFirst
    ? MEMBERSHIP_STEP_ORDER
    : STANDARD_STEP_ORDER;
}

// Sync session throttling state
let lastSyncTime = 0;
let syncBackoffMs = 0;
const MIN_SYNC_INTERVAL_MS = 1000; // Minimum 1 second between syncs
const MAX_BACKOFF_MS = 30000; // Maximum 30 second backoff
const BACKOFF_MULTIPLIER = 2;

// Type for the persisted subset of state
interface PersistedCartState {
  sessionId: string | null;
  language: BookingLanguage;
  market: MarketCountry;
}

// Helper to generate a new session ID
function generateSessionId(): string {
  // Use crypto.randomUUID if available, otherwise fall back to manual UUID generation
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers or non-secure contexts
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Helper to get session ID from cookie
function getSessionIdFromCookie(): string | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === SESSION_COOKIE_NAME) {
      return value;
    }
  }
  return null;
}

// Helper to set session ID in cookie (1 year expiry)
function setSessionIdCookie(sessionId: string): void {
  const expiryDate = new Date();
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);
  document.cookie = `${SESSION_COOKIE_NAME}=${sessionId}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
}

// Helper to clear session ID cookie
function clearSessionIdCookie(): void {
  document.cookie = `${SESSION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

// Helper to fetch external slots for a clinic directly (not via React Query)
async function fetchExternalSlotsForClinic(
  clinicId: string,
): Promise<ExternalSlot[]> {
  const today = format(new Date(), "yyyy-MM-dd");

  const { data, error } = await supabase
    .from("clinic_external_slots")
    .select("slot_date, times, fetched_at")
    .eq("clinic_id", clinicId)
    .gte("slot_date", today)
    .order("slot_date", { ascending: true });

  if (error) {
    console.error("[BookingFlow] Failed to fetch external slots:", error);
    return [];
  }

  return (data as ExternalSlot[]) || [];
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useBookingFlowStore = create<BookingFlowState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ========================================================================
        // INITIALIZATION ACTIONS
        // ========================================================================

        setProducts: (products) => {
          if (get()._products === products) return;
          console.log("[BookingFlow] Setting products:", products.length);
          set({ _products: products });

          // A cart restored before the catalog loaded may hold products this
          // market no longer sells.
          const pruned = dropUnavailableCartItems(
            get().selectedProducts,
            get().selectedAddOns,
            products,
          );
          if (pruned) {
            console.log("[BookingFlow] Dropping cart items no longer sold");
            set(pruned);
          }

          // Complete a deferred empty-session URL apply that was waiting
          // for products to load (see loadSession). AHD-5620
          if (get()._pendingEmptyUrlApply && products.length > 0) {
            const ids = get()._conflictUrlProducts;
            if (ids.length > 0) {
              console.log(
                "[BookingFlow] Applying deferred URL products now that products are loaded:",
                ids,
              );
              const urlPackages = ids.filter((id) => {
                const p = products.find((prod) => prod.id === id);
                return !p || p.product_type === "package";
              });
              const urlAddOns = ids.filter((id) => {
                const p = products.find((prod) => prod.id === id);
                return p && p.product_type !== "package";
              });
              const urlPackage = products.find(
                (p) =>
                  urlPackages.includes(p.id) && p.product_type === "package",
              );

              set({
                _conflictUrlProducts: [],
                _pendingEmptyUrlApply: false,
                selectedProducts: urlPackages,
                selectedAddOns: urlAddOns,
                selectedPackageId: urlPackage?.id || null,
                selectedPackageType: urlPackage?.package_types?.[0] || "mri",
                viewMode: urlPackage ? "packages" : "configure",
              });
              updateUrlWithBookingParams({ products: "" });
              // Open the step the link asked for, as a fresh visit does
              // (Eugene, 22.10): the one-off handoff's add-ons, the quiz's clinic.
              get()._openCartEntryStep(!!urlPackage);
            } else {
              set({ _pendingEmptyUrlApply: false });
            }
          }
        },

        setClinics: (clinics) => {
          if (get()._clinics === clinics) return;
          console.log("[BookingFlow] Setting clinics:", clinics.length);
          const previousClinicsLength = get()._clinics.length;
          set({ _clinics: clinics });

          // If clinics just loaded and we have pending time slots, validate them now
          if (previousClinicsLength === 0 && clinics.length > 0) {
            const { selectedTimeSlots, selectedClinic } = get();
            if (selectedTimeSlots.length > 0 && selectedClinic) {
              console.log(
                "[BookingFlow] Clinics loaded - running deferred time slot validation",
              );
              get()._validateTimeSlotsForClinic();
            }
          }
        },

        setClinicProductAvailability: (availability) => {
          if (get()._clinicProductAvailability === availability) return;
          console.log(
            "[BookingFlow] Setting clinic product availability:",
            availability.length,
          );
          set({ _clinicProductAvailability: availability });
        },

        setClinicExternalSlots: (clinicId, slots) => {
          const { _clinicExternalSlots } = get();
          if (_clinicExternalSlots[clinicId] === slots) return;
          console.log(
            "[BookingFlow] Setting external slots for clinic:",
            clinicId,
            "count:",
            slots.length,
          );

          set({
            _clinicExternalSlots: {
              ..._clinicExternalSlots,
              [clinicId]: slots,
            },
          });

          // SIDE EFFECT: Validate time slots if this is the currently selected clinic
          // Must run regardless of current step — external slots arrive asynchronously
          // and the user may have gone back to clinic step with stale time slots
          const { selectedClinic, selectedTimeSlots } = get();
          if (selectedClinic === clinicId && selectedTimeSlots.length > 0) {
            get()._validateTimeSlotsForClinic();
          }
        },

        setLanguage: (language) => {
          console.log("[BookingFlow] Setting language:", language);
          set({ language });
          // Also update localStorage so useLanguage hook stays in sync
          safeLocalStorage.setItem("userLanguage", language);
        },

        setMarket: (market) => {
          const current = get().market;
          if (market === current) return;

          console.log("[BookingFlow] Switching market:", current, "→", market);

          // Switching country invalidates every downstream selection (clinics,
          // prices and clinic-specific availability all differ per market), so
          // we restart the booking from the first step with a fresh session.
          // Language is intentionally left untouched — market and language are
          // independent.
          get().resetFlow();
          // Reset gate: switching market always drops out of the membership
          // (cadence) path so CH→DE never leaves the user stuck mid-cadence.
          useMembershipPlanStore.getState().setActive(false);
          useMembershipPlanStore.getState().setMembershipFirst(false);
          const freshSessionId = generateSessionId();
          setSessionIdCookie(freshSessionId);
          set({
            market,
            sessionId: freshSessionId,
            hasInitializedFromUrl: true,
          });
          // French is Switzerland-only: a CH→DE switch coerces a French session
          // to the DE-market default (German), since FR isn't offered there.
          const coercedLanguage = coerceLanguageForMarket(
            market,
            get().language,
          );
          if (coercedLanguage !== get().language) {
            get().setLanguage(coercedLanguage);
          }
          replaceStepInUrl("product");
          updateUrlWithBookingParams(marketUrlParams(market));
          // Persist the fresh, market-scoped session to the server.
          get().syncSession("marketChanged");
          trackEvent({ event: "market_changed", props: { market } });
        },

        initializeFromUrl: (products, isBuildYourOwnEnabled = true) => {
          const { hasInitializedFromUrl } = get();

          // ?from=<source> becomes ?products=<uuid> HERE, before anything else
          // in this function or in loadSession reads the URL.
          //
          // It is an alias onto the existing ?products= path, and rewriting the
          // param is what makes it one: every later reader then sees it for
          // free, including the three that run before the old injection point
          // and read window.location directly — the loadSession-in-progress
          // stash below, the session-conflict check, and loadSession itself.
          // Resolved after those, the deep link was silently dropped for anyone
          // with a restored sessionId, i.e. every returning customer, and a
          // follow-up CTA on a health report has no other audience.
          //
          // Precedence lives in entrySourceUrlProducts: an explicit ?products=
          // or ?packageType= wins, because from= only supplies defaults.
          const entryProducts = entrySourceUrlProducts(
            window.location.search,
            products,
          );
          if (entryProducts) {
            console.log(
              "[BookingFlow] Entry source preselecting package:",
              entryProducts,
            );
            updateUrlWithBookingParams({ products: entryProducts });
          }

          // Never allow staying on confirmation page on refresh - redirect to product
          const urlStep = getStepFromUrl();
          if (urlStep === "confirmation") {
            console.log(
              "[BookingFlow] Confirmation page refresh detected, redirecting to product",
            );
            replaceStepInUrl("product");
            set({ currentStep: "product", hasInitializedFromUrl: true });
            get().resetFlow();
            return;
          }

          // If loadSession is in progress (async), stash URL products for conflict check later
          if (get().sessionId && get().isLoading) {
            const urlProductIds = getProductIdsFromUrl();
            if (urlProductIds.length > 0) {
              console.log(
                "[BookingFlow] loadSession in progress, stashing URL products for conflict check:",
                urlProductIds,
              );
              set({ _conflictUrlProducts: urlProductIds });
              updateUrlWithBookingParams({ products: "" });
            }
            return;
          }

          // An explicit ?country=X that differs from the active market is
          // authoritative: a deep link into another market wins over any
          // persisted/in-progress flow. Switch markets and wipe the other
          // market's progress. This MUST run before the hasInitializedFromUrl
          // early-return, otherwise a persisted Swiss session ignores a fresh
          // ?country=de link (the symptom we're fixing).
          const urlMarketOverride = parseMarketFromUrl(
            new URLSearchParams(window.location.search),
          );
          if (urlMarketOverride && urlMarketOverride !== get().market) {
            console.log(
              "[BookingFlow] ?country override differs from active market; resetting to",
              urlMarketOverride,
            );
            set({ market: urlMarketOverride });
            get().resetFlow(); // keeps the just-set market, clears the rest
            set({ hasInitializedFromUrl: false });
            // French is Switzerland-only: a ?country=de override coerces a French
            // session (e.g. ?country=de&locale=fr) to the DE-market default.
            const coerced = coerceLanguageForMarket(
              urlMarketOverride,
              get().language,
            );
            if (coerced !== get().language) {
              get().setLanguage(coerced);
            }
            // fall through to a fresh initialization for the new market
          }

          if (get().hasInitializedFromUrl) {
            const urlProductIds = getProductIdsFromUrl();
            // A persisted session restores sessionId/market/language but NOT
            // selectedProducts (see PersistedCartState). So "already initialized
            // but nothing selected" is a real state on any return visit. If the
            // URL also carries deep-link products and the catalog is now loaded,
            // this is NOT a conflict — there's nothing to conflict with. Fall
            // through to the selection logic below and apply them, instead of
            // flagging a phantom conflict and stripping the products (which left
            // the deep link selecting nothing).
            const initializedButEmpty =
              urlProductIds.length > 0 &&
              !!products &&
              get().selectedProducts.length === 0;
            if (!initializedButEmpty) {
              // Session was already loaded. Check if URL has DIFFERENT products.
              const sessionId = get().sessionId;
              if (sessionId && urlProductIds.length > 0) {
                const sessionProducts = get().selectedProducts;
                const hasNewProducts = urlProductIds.some(
                  (id) => !sessionProducts.includes(id),
                );
                if (hasNewProducts) {
                  console.log(
                    "[BookingFlow] Session conflict detected - URL has different products",
                    { sessionProducts, urlProductIds },
                  );
                  set({
                    pendingSessionConflict: true,
                    _conflictSessionProducts: sessionProducts,
                    _conflictSessionAddOns: get().selectedAddOns,
                    _conflictUrlProducts: urlProductIds,
                  });
                  updateUrlWithBookingParams({ products: "" });
                  return;
                }
                // URL products are subset of session → no conflict, navigate to
                // addons, or to clinic when the link asked for it (cartEntryStep)
                updateUrlWithBookingParams({ products: "" });
                // Before goToStep reads the step order: the one-off handoff
                // books once, whatever membership the session restored.
                get()._dropMembershipOnOneTimeHandoff();
                const sessionEntryStep =
                  cartEntryStep(!!get().selectedPackageId) ?? "addons";
                console.log(
                  "[BookingFlow] URL products already in session, navigating to",
                  sessionEntryStep,
                );
                get().goToStep(sessionEntryStep, { skipValidation: true });
                return;
              }
              console.log(
                "[BookingFlow] Already initialized from URL, skipping",
              );
              return;
            }
            // initializedButEmpty → fall through to the URL-product selection below
          }

          const urlProductIds = getProductIdsFromUrl();
          const urlParams = new URLSearchParams(window.location.search);
          const urlPackageType = urlParams.get("packageType");
          const testModeParams = getTestModeParams();

          // Deep-link the market via ?country=de / ?country=ch (Framer links).
          const urlMarket = parseMarketFromUrl(urlParams);
          if (urlMarket && urlMarket !== get().market) {
            console.log("[BookingFlow] Setting market from URL:", urlMarket);
            set({ market: urlMarket });
          }

          // Force-show package types hidden by the market via ?force=mri.
          const forced = parseForcedPackageTypes(urlParams);
          if (forced.length > 0) {
            console.log(
              "[BookingFlow] Forcing package types from URL:",
              forced,
            );
          }
          set({ forcedPackageTypes: forced });

          if (urlPackageType) {
            console.log(
              "[BookingFlow] Setting package type from URL:",
              urlPackageType,
            );
            set({ selectedPackageType: urlPackageType });
          }

          const urlSessionId = getUrlParam("sessionId");
          if (urlSessionId && urlSessionId !== get().sessionId) {
            console.log(
              "[BookingFlow] Found new sessionId in URL, loading from server:",
              urlSessionId,
            );
            get().loadSession(urlSessionId);
            // We return early because loadSession will set the state
            return;
          }

          const testProduct = testModeParams.product;
          const testAddOns = testModeParams.addOns || [];
          const testClinic = testModeParams.clinic;
          const testTimeSlots = testModeParams.timeSlots;
          const hasTestModePreselection =
            testProduct || testAddOns.length > 0 || testClinic || testTimeSlots;

          if (hasTestModePreselection && products) {
            console.log("[BookingFlow] Test mode preselection detected:", {
              testProduct,
              testAddOns,
              testClinic,
              testTimeSlots,
            });

            if (testProduct) {
              const packageProduct = products.find(
                (p) => p.id === testProduct && p.product_type === "package",
              );

              if (packageProduct) {
                if (
                  packageProduct.package_types &&
                  packageProduct.package_types.length > 0
                ) {
                  const inferredType = packageProduct.package_types[0];
                  console.log(
                    "[BookingFlow] Inferring package type from preselected product:",
                    inferredType,
                  );
                  set({ selectedPackageType: inferredType });
                }

                set({
                  viewMode: "packages",
                  selectedPackageId: testProduct,
                  hasInitializedFromUrl: true,
                });

                get().selectProducts([testProduct], { skipNavigation: true });
              } else {
                const product = products.find((p) => p.id === testProduct);
                if (product) {
                  set({
                    viewMode: isBuildYourOwnEnabled ? "configure" : "packages",
                    selectedProducts: [testProduct],
                    hasInitializedFromUrl: true,
                  });
                }
              }
            } else {
              set({ hasInitializedFromUrl: true });
            }

            if (testAddOns.length > 0) {
              const validAddOns = testAddOns.filter((id) =>
                products.some(
                  (p) => p.id === id && p.product_type !== "package",
                ),
              );
              if (validAddOns.length > 0) {
                console.log("[BookingFlow] Preselecting add-ons:", validAddOns);
                set({ selectedAddOns: validAddOns });
              }
            }

            if (testClinic) {
              console.log("[BookingFlow] Preselecting clinic:", testClinic);
              set({ selectedClinic: testClinic });
            }

            if (testTimeSlots && testTimeSlots.length > 0) {
              try {
                // Use the first time slot's date as the selected date
                const firstSlot = testTimeSlots[0];
                const [year, month, day] = firstSlot.date
                  .split("-")
                  .map(Number);
                const firstDate = new Date(year, month - 1, day);
                console.log("[BookingFlow] Preselecting date:", firstDate);
                // URL-injected dates are considered manually selected (explicit user/system input)
                set({ selectedDate: firstDate, wasDateManuallySelected: true });

                // Convert all time slots to the store format
                const timeSlots = testTimeSlots.map((slot, index) => {
                  const [y, m, d] = slot.date.split("-").map(Number);
                  return {
                    date: new Date(y, m - 1, d),
                    time: slot.time,
                    preference: (index === 0
                      ? "first"
                      : index === 1
                        ? "second"
                        : "third") as "first" | "second" | "third",
                  };
                });
                console.log(
                  "[BookingFlow] Preselecting time slots:",
                  timeSlots,
                );
                set({ selectedTimeSlots: timeSlots });
              } catch (e) {
                console.error(
                  "[BookingFlow] Failed to parse timeSlots:",
                  testTimeSlots,
                  e,
                );
              }
            }

            return;
          }

          // ?packageType=membership — the landing-page entry into the membership
          // flow. The landing page has already sold the plan, so skip straight
          // to cadence instead of parking the user on the degenerate one-card
          // "Ahead Mitgliedschaft" product page that this packageType renders.
          //
          // Note the package type is stored as "blood-only", NOT "membership":
          // that leaves the real two-card "membership vs one-off" comparison as
          // the product step underneath, so back-navigation and the order-summary
          // edit pencil land somewhere useful instead of on the one-card page.
          if (urlPackageType === "membership") {
            // Needs the catalog to resolve the baseline. Leave
            // hasInitializedFromUrl=false so the next call (once `products`
            // lands) does the work, same as the URL-product path below.
            if (!products) return;

            // pickBaselineProduct, NOT isBloodMembershipBaseProduct: the latter
            // identifies the baseline inside a CART and doesn't require
            // product_type === "package", so on the raw catalog it can return a
            // blood add-on. The cadence step needs a real package or it bounces.
            // This is the same picker the "Mitglied werden" CTA and the
            // follow-up splash already use, so all three land on one product.
            const baseline = pickBaselineProduct(products);
            if (baseline) {
              // A cart from a previous visit must not be silently replaced —
              // hand it to SessionConflictDialog and let the user choose.
              // resolveSessionConflict("url") re-reads ?packageType to land on
              // cadence rather than addons.
              const sessionProducts = get().selectedProducts;
              const hasOtherCart =
                sessionProducts.length > 0 &&
                !sessionProducts.includes(baseline.id);
              if (hasOtherCart) {
                console.log(
                  "[BookingFlow] Membership entry conflicts with existing cart",
                  { sessionProducts, baseline: baseline.id },
                );
                set({
                  pendingSessionConflict: true,
                  _conflictSessionProducts: sessionProducts,
                  _conflictSessionAddOns: get().selectedAddOns,
                  _conflictUrlProducts: [baseline.id],
                  hasInitializedFromUrl: true,
                });
                return;
              }

              console.log(
                "[BookingFlow] Membership entry — going straight to cadence",
              );
              useMembershipPlanStore.getState().setActive(true);
              // membershipFirst — NOT just active — is what getActiveStepOrder
              // keys on, and this entry lands on `cadence`, a step that exists
              // only in MEMBERSHIP_STEP_ORDER. Leaving it false made the step
              // order the standard 5, where indexOf("cadence") is -1: canGoBack
              // returned false (no back button on desktop) and goBack() was a
              // no-op (mobile's back click did nothing), stranding anyone who
              // arrived via ?packageType=membership on the cadence step.
              useMembershipPlanStore.getState().setMembershipFirst(true);
              set({
                selectedProducts: [baseline.id],
                selectedPackageId: baseline.id,
                selectedPackageType: "blood-only",
                viewMode: "packages",
                currentStep: "cadence",
                hasInitializedFromUrl: true,
              });
              replaceStepInUrl("cadence");
              return;
            }
          }

          if (urlProductIds.length > 0 && products) {
            console.log(
              "[BookingFlow] Initializing from URL products:",
              urlProductIds,
            );

            // Apply URL products directly — set the right mode (MRI/blood) and stay on product step
            const urlPackages = urlProductIds.filter((id) => {
              const p = products.find((prod) => prod.id === id);
              return !p || p.product_type === "package";
            });
            const urlAddOns = urlProductIds.filter((id) => {
              const p = products.find((prod) => prod.id === id);
              return p && p.product_type !== "package";
            });
            const urlPackage = products.find(
              (p) => urlPackages.includes(p.id) && p.product_type === "package",
            );

            const inferredType =
              urlPackage?.package_types?.[0] || get().selectedPackageType;

            set({
              selectedProducts: urlPackages,
              selectedAddOns: urlAddOns,
              selectedPackageId: urlPackage?.id || null,
              selectedPackageType: inferredType,
              viewMode: urlPackage ? "packages" : "configure",
              currentStep: "product",
              hasInitializedFromUrl: true,
            });
            updateUrlWithBookingParams({ products: "" });

            // Before the step order is read: the one-off handoff walks the
            // standard funnel, whatever membership an earlier load left on.
            get()._dropMembershipOnOneTimeHandoff();
            // A cart link that asked for the clinic step (cartEntryStep) opens
            // there, with product + add-ons as history so Prev lands on the
            // add-ons the caller preselected.
            get()._openCartEntryStep(!!urlPackage);
          } else if (urlProductIds.length === 0) {
            set({ hasInitializedFromUrl: true });
          }
          // else: the URL carries deep-link products but the catalog hasn't
          // loaded yet. Leave hasInitializedFromUrl=false so the next call (once
          // `products` arrives) applies them above, rather than marking init
          // "done" with nothing selected.
        },

        initializeTestModeNavigation: () => {
          const { currentStep, selectedDate } = get();
          const testModeParams = getTestModeParams();

          if (!testModeParams.jumpToLastStep) {
            return;
          }

          if (currentStep === "details") {
            console.log(
              "[BookingFlow] Test mode: Already on details step, skipping",
            );
            return;
          }

          // Validate that the selected date is not in the past before jumping to details
          if (selectedDate) {
            const selectedDateStart = startOfDay(selectedDate);
            const today = startOfDay(new Date());
            if (isBefore(selectedDateStart, today)) {
              console.log(
                "[BookingFlow] Test mode: Cannot jump to details - selected date is in the past",
              );
              // Clear invalid date and go to date step instead
              set({
                selectedDate: null,
                selectedTimeSlots: [],
                currentStep: "date",
                stepHistory: getActiveStepOrder().slice(
                  0,
                  getActiveStepOrder().indexOf("date"),
                ) as BookingStep[],
              });
              replaceStepInUrl("date");
              return;
            }
          }

          console.log(
            "[BookingFlow] Test mode: Jumping to last booking step (details)",
          );

          set({
            currentStep: "details",
            stepHistory: getActiveStepOrder().slice(
              0,
              getActiveStepOrder().indexOf("details"),
            ) as BookingStep[],
          });

          scrollToTopDelayed(40);

          const { selectedPackageType: pkgType } = get();
          trackEvent({
            event: "step_view",
            props: {
              step: "details",
              from: "test_mode_jump",
              product_type:
                pkgType === "blood-only" ? "blood_only" : "mri_package",
            },
          });
        },

        // ========================================================================
        // STEP NAVIGATION ACTIONS
        // ========================================================================

        goToStep: (step, options = {}) => {
          const { currentStep, stepHistory, validateStep } = get();
          console.log(
            "[BookingFlow] Navigation: Attempting to go from",
            currentStep,
            "to",
            step,
          );

          // GUARD: Skip if already on the same step
          if (currentStep === step) {
            console.log(
              "[BookingFlow] Navigation skipped: Already on step",
              step,
            );
            return;
          }

          // GUARD: Validate current step unless skipped
          if (!options.skipValidation) {
            const validation = validateStep(currentStep);
            if (!validation.canProceed) {
              console.log(
                "[BookingFlow] Navigation blocked: Current step validation failed",
                validation.errors,
              );
              get().showToast({
                message: validation.errors[0] || "Please complete this step",
                type: "error",
                duration: 3000,
              });
              return;
            }
          }

          // No special handling - clinic step is required for all package types
          let targetStep = step;

          // NEW MODEL: Ensure the target step is actually valid to enter
          const validStep = get().ensureStepValidity(targetStep);
          if (validStep !== targetStep) {
            console.log(
              "[BookingFlow] Navigation: Redirecting to valid step",
              validStep,
              "instead of",
              targetStep,
            );
            targetStep = validStep;

            // Show errors for the step we roll back to
            const errors = get().getMissingRequirements(targetStep);
            if (errors.length > 0) {
              get().showToast({
                message: errors[0],
                type: "warning",
              });
            }
          }

          // UPDATE: Add to history and change step
          const newHistory = [...stepHistory, currentStep];
          set({
            currentStep: targetStep,
            stepHistory: newHistory,
          });

          // SIDE EFFECT: Push to browser history (unless skipHistory is true)
          if (!options.skipHistory) {
            if (targetStep === "confirmation") {
              // Replace instead of push: booking is final, entitlement/session
              // are consumed, so back must not bounce into a stale flow state.
              replaceStepInUrl(targetStep);
            } else {
              pushStepToHistory(targetStep);
            }
          }

          // SIDE EFFECT: Scroll to top
          if (!options.skipScroll) {
            scrollToTopDelayed(40);
          }

          // DEFERRED SIDE EFFECTS: Move analytics + session sync out of the
          // synchronous click handler so GTM/PostHog/fraud0 processing doesn't
          // block React rendering (~1400ms savings on step transitions).
          setTimeout(() => {
            // Track navigation
            const { selectedPackageType } = get();
            const productType =
              selectedPackageType === "blood-only"
                ? "blood_only"
                : "mri_package";
            trackEvent({
              event: "step_view",
              props: {
                step: targetStep,
                from: currentStep,
                product_type: productType,
              },
            });

            // Sync to server
            if (targetStep === "confirmation") {
              setTimeout(async () => {
                await get().syncSession("goToStep-confirmation");
                console.log(
                  "[BookingFlow] Confirmation synced, clearing session for next visit",
                );
                set({ sessionId: null });
                clearSessionIdCookie();
              }, 500);
            } else {
              get().syncSession("goToStep");
            }
          }, 0);

          console.log("[BookingFlow] Navigation successful:", targetStep);
        },

        setStepFromHistory: (step) => {
          const {
            currentStep,
            stepHistory,
            selectedProducts,
            selectedClinic,
            selectedDate,
            selectedTimeSlots,
            bookingId,
          } = get();
          console.log(
            "[BookingFlow] setStepFromHistory: Setting step from browser history:",
            step,
          );

          // Build appropriate history based on the step we're going to.
          // Use the active path (membership cadence vs standard add-ons).
          const stepOrder = getActiveStepOrder();
          const targetIndex = stepOrder.indexOf(step);
          const currentIndex = stepOrder.indexOf(currentStep);

          if (targetIndex === -1) {
            console.log(
              "[BookingFlow] setStepFromHistory: Invalid step:",
              step,
            );
            return;
          }

          // Check if we have the required data for the target step
          const validStep = get().ensureStepValidity(step);

          if (validStep !== step) {
            console.log(
              "[BookingFlow] setStepFromHistory: Missing required data for step:",
              step,
              "- resetting to valid step",
              validStep,
            );
            set({
              currentStep: validStep,
              stepHistory: [], // Reset history if we're forced back
            });
            // Update URL to reflect the reset
            replaceStepInUrl(validStep);
            scrollToTopDelayed(40);
            return;
          }

          // Build history as all steps before the target step
          const newHistory = stepOrder.slice(0, targetIndex);

          set({
            currentStep: step,
            stepHistory: newHistory,
            // Reset submission state when navigating back via browser history
            // (fixes stuck "submitting" button after back from confirmation)
            isSubmitting: false,
            isSubmitted: false,
          });

          // Scroll to top
          scrollToTopDelayed(40);

          // Track browser history navigation
          get().syncSession("historyNavigation");
        },

        goBack: () => {
          const { currentStep, stepHistory } = get();
          const stepOrder = getActiveStepOrder();
          const currentIndex = stepOrder.indexOf(currentStep);

          if (currentIndex > 0) {
            const previousStep =
              stepHistory.length > 0
                ? stepHistory[stepHistory.length - 1]
                : stepOrder[currentIndex - 1];
            const newHistory = stepHistory.slice(0, -1);

            console.log("[BookingFlow] Going back to:", previousStep);
            set({
              currentStep: previousStep,
              stepHistory: newHistory,
            });

            // Update URL
            replaceStepInUrl(previousStep);
            scrollToTopDelayed(40);

            // Track back navigation
            get().syncSession("goBack");
          }
        },

        canGoBack: () => {
          const { stepHistory, currentStep } = get();
          // If stepHistory is populated, use it
          if (stepHistory.length > 0) {
            return true;
          }
          // Otherwise, infer from currentStep (handles rehydration from localStorage
          // where stepHistory is not persisted)
          const stepOrder = getActiveStepOrder();
          const currentIndex = stepOrder.indexOf(currentStep);
          return currentIndex > 0;
        },

        // ========================================================================
        // PRODUCT SELECTION ACTIONS
        // ========================================================================

        setViewMode: (mode) => {
          console.log("[BookingFlow] Setting view mode:", mode);
          set({ viewMode: mode });
        },

        setSelectedPaymentMode: (mode) =>
          // Picking a mode clears any "you must choose" error. Backing out of
          // the member option also invalidates any previously-initiated
          // membership checkout (opened the payment modal, aborted, picked
          // pay-later) — otherwise the confirmation snapshot would still
          // claim a membership purchase.
          set({
            selectedPaymentMode: mode,
            paymentModeError: false,
            ...(mode !== "yearly" ? { membershipCheckoutInitiated: null } : {}),
          }),

        setPaymentModeError: (hasError) => set({ paymentModeError: hasError }),

        markMembershipCheckoutInitiated: (source) => {
          set({ membershipCheckoutInitiated: source });
          // Persist immediately: TWINT/Klarna/PayPal full-page-reload on
          // return, and no step transition happens between this click and the
          // redirect — without an explicit sync the restored confirmation
          // snapshot would lose the marker.
          get().syncSession("membership-checkout-initiated");
        },

        setSelectedPackageType: (packageType) => {
          console.log("[BookingFlow] Setting package type:", packageType);
          const { selectedPackageType: currentType } = get();
          if (packageType === currentType) return;
          // Reset dependent selections when switching package type. Payment mode
          // resets to null (nothing pre-selected) so a freshly-switched cart never
          // shows a membership the user hasn't opted into — see the default above.
          set({
            selectedPackageType: packageType,
            selectedProducts: [],
            selectedPackageId: null,
            selectedAddOns: [],
            selectedPaymentMode: null,
            // A new package type is a new basket — any initiated membership
            // checkout belonged to the old one.
            membershipCheckoutInitiated: null,
          });

          // The membership (cadence) path only makes sense for a blood-only
          // cart. Switching to any other type (e.g. MRI) must drop it, or the
          // user ends up in a "membership" booking for a radiology package: the
          // line item shows the cadence price (CHF 299) while the total, the
          // Stripe charge and the voucher base all use the real package price
          // (CHF 1890). Mirrors the same reset in setMarket. (AHD-7853)
          // Drop membership when leaving the blood-only flow — UNLESS the user is
          // deliberately building a scan+membership bundle, in which case the
          // membership rides along onto the mri tab.
          const { bundleAttached } = useMembershipPlanStore.getState();
          if (packageType !== "blood-only" && !bundleAttached) {
            useMembershipPlanStore.getState().setActive(false);
            useMembershipPlanStore.getState().setMembershipFirst(false);
          }

          // Sync to server immediately to prevent refresh from overwriting
          get().syncSession("package type changed");
        },

        attachScanToMembership: (packageId) => {
          // Membership-first → user adds a full-body scan. Keep membership active
          // and its cadence; move to the mri tab and select the scan. bundleAttached
          // makes setSelectedPackageType's reset preserve the membership.
          // Remember the blood baseline we're replacing so detachScanFromMembership
          // can restore it (only on the first attach — a scan→scan switch keeps it).
          if (get().selectedPackageType === "blood-only") {
            const baseline = get()._products.find(
              (p) =>
                p.product_type === "package" &&
                get().selectedProducts.includes(p.id),
            );
            set({ bloodBaselineBeforeScanId: baseline?.id ?? null });
          }
          useMembershipPlanStore.getState().setBundleAttached(true);
          useMembershipPlanStore.getState().setActive(true);
          // Set the tab and package ID directly (avoids setSelectedPackageType's
          // selectedProducts/selectedPackageId wipe that would clear what we're
          // about to select). selectedProducts is set via the normal selectProducts
          // path below so the card highlight, canProceed gate, add-to-cart
          // tracking and incompatible-addon pruning all follow the same code path
          // as a direct package pick on the mri tab. Add-on picks survive the
          // switch — selectProducts prunes only the incompatible ones.
          set({
            selectedPackageType: "mri",
            selectedPackageId: packageId,
            selectedPaymentMode: "yearly",
          });
          // Use the standard selection path so all downstream effects
          // (add_to_cart tracking, addon pruning, date recalculation) fire.
          get().selectProducts([packageId]);
          // skipScroll: the scan is picked ON the add-ons page, so jumping to
          // the top would yank the page out from under the user's click.
          get().goToStep("addons", {
            skipValidation: true,
            skipScroll: true,
          });
          get().syncSession("scan attached to membership");
        },

        detachScanFromMembership: () => {
          // Inverse of attachScanToMembership: drop the scan, keep the
          // membership, restore the blood baseline package so the cart and
          // add-ons page return to the membership-only state.
          const baselineId =
            get().bloodBaselineBeforeScanId ??
            get()._products.find(
              (p) =>
                p.product_type === "package" &&
                (p.package_types ?? []).includes("blood-only") &&
                p.status === "active",
            )?.id;
          if (!baselineId) return;
          useMembershipPlanStore.getState().setBundleAttached(false);
          set({
            selectedPackageType: "blood-only",
            selectedPackageId: baselineId,
          });
          get().selectProducts([baselineId]);
          get().syncSession("scan detached from membership");
        },

        detachMembership: () => {
          // Fully exit the membership: clear the plan store flags (active drives
          // the bundle; bundleAttached lets the scan survive tab-switches;
          // membershipFirst drives the 7-step flow) and reset the payment mode.
          // Persist immediately — the plan flags ride in the server session
          // snapshot, so without a sync a back-nav / loadSession would restore the
          // membership we just removed (the "Prev re-adds membership" bug).
          const planStore = useMembershipPlanStore.getState();
          planStore.setActive(false);
          planStore.setBundleAttached(false);
          planStore.setMembershipFirst(false);
          set({ selectedPaymentMode: null, paymentModeError: false });
          get().syncSession("membership removed");
        },

        selectProducts: (products, options = {}) => {
          console.log("[BookingFlow] Selecting products:", products);
          const { selectedAddOns, _products, language, selectedProducts } =
            get();

          // Check if a package was selected
          const newPackage = _products.find(
            (p) => p.product_type === "package" && products.includes(p.id),
          );

          if (newPackage) {
            let incompatibleAddOns: string[] = [];
            let removalReason = "";

            // 1. Check if add-ons are incompatible with the new package type
            if (
              newPackage.package_types &&
              newPackage.package_types.length > 0
            ) {
              const newPackageType = newPackage.package_types[0]; // Packages have single type

              const typeIncompatibleAddOns = selectedAddOns.filter(
                (addOnId) => {
                  const addOn = _products.find((p) => p.id === addOnId);
                  if (!addOn) return false;

                  const supportedTypes = addOn.package_types || [];
                  // If empty, it supports all types
                  if (supportedTypes.length === 0) return false;

                  // Return true if this add-on doesn't support the new package type
                  return !supportedTypes.includes(newPackageType);
                },
              );

              if (typeIncompatibleAddOns.length > 0) {
                incompatibleAddOns = [
                  ...incompatibleAddOns,
                  ...typeIncompatibleAddOns,
                ];
                removalReason = "type_incompatible";
              }
            }

            // 2. Check if add-ons are already included in the new package
            const addOnsAlreadyInPackage = selectedAddOns.filter((addOnId) => {
              return newPackage.components?.includes(addOnId);
            });

            if (addOnsAlreadyInPackage.length > 0) {
              incompatibleAddOns = [
                ...new Set([...incompatibleAddOns, ...addOnsAlreadyInPackage]),
              ];
              removalReason =
                addOnsAlreadyInPackage.length === incompatibleAddOns.length
                  ? "already_included"
                  : "mixed";
            }

            // Remove duplicates
            incompatibleAddOns = [...new Set(incompatibleAddOns)];

            // Show warning and remove incompatible/duplicate add-ons
            if (incompatibleAddOns.length > 0) {
              const incompatibleNames = incompatibleAddOns
                .map((id) => _products.find((p) => p.id === id)?.name || id)
                .join(", ");

              const lng = languageMap[language];
              let message = "";
              if (removalReason === "already_included") {
                message = i18next.t("toast.addonsAlreadyIncluded", {
                  lng,
                  names: incompatibleNames,
                });
              } else if (removalReason === "type_incompatible") {
                message = i18next.t("toast.addonsTypeIncompatible", {
                  lng,
                  names: incompatibleNames,
                });
              } else {
                message = i18next.t("toast.addonsIncompatibleOrIncluded", {
                  lng,
                  names: incompatibleNames,
                });
              }

              console.log(
                "[BookingFlow] Removing incompatible/duplicate add-ons:",
                incompatibleAddOns,
              );
              toast.warning(message, {
                duration: 5000,
                position: "bottom-left",
              });

              // Remove incompatible add-ons
              const compatibleAddOns = selectedAddOns.filter(
                (id) => !incompatibleAddOns.includes(id),
              );
              set({ selectedAddOns: compatibleAddOns });
            }

            // Track add_to_cart for newly selected products (packages)
            const newlySelectedProducts = products.filter(
              (id) => !selectedProducts.includes(id),
            );
            if (newlySelectedProducts.length > 0) {
              const currentAddOns = get().selectedAddOns;
              const allCartIds = [...products, ...currentAddOns];
              const allCartProducts = allCartIds
                .map((id) => _products.find((p) => p.id === id))
                .filter((p): p is NonNullable<typeof p> => Boolean(p));
              const totalValue = allCartProducts.reduce(
                (sum, p) =>
                  sum +
                  (p.default_price ? p.default_price.unit_amount / 100 : 0),
                0,
              );
              trackEvent({
                event: "add_to_cart",
                props: {
                  // Discriminates package selection from add-on toggles (PostHog add-on funnel)
                  cart_action: "package",
                  ecommerce: {
                    items: allCartProducts.map((p) => ({
                      item_id: p.id,
                      item_name: p.name || p.translations?.name || "Unknown",
                      item_category:
                        p.category?.name ||
                        (p.product_type === "package" ? "Package" : "Add-on"),
                      price: p.default_price
                        ? p.default_price.unit_amount / 100
                        : 0,
                      currency: p.default_price?.currency || "CHF",
                      quantity: 1,
                    })),
                    value: totalValue,
                    currency:
                      allCartProducts[0]?.default_price?.currency || "CHF",
                  },
                },
              });
            }
          }

          // UPDATE: Set products
          set({ selectedProducts: products });

          // SIDE EFFECT: Recalculate first available date and validate current date
          // Must run regardless of current step — if the user goes back to product
          // and switches products, the previously selected date may no longer be valid
          if (get().selectedDate) {
            get()._recalculateAndValidateDate();
          }

          // NOTE: Auto-navigation removed - user must click "Continue" button
          // This provides better UX and prevents unexpected navigation
          // Session sync happens on step change, not on selection
        },

        addProduct: (productId) => {
          const { selectedProducts } = get();
          if (!selectedProducts.includes(productId)) {
            const newProducts = [...selectedProducts, productId];
            console.log("[BookingFlow] Adding product:", productId);
            set({ selectedProducts: newProducts });

            // SIDE EFFECT: Recalculate date
            get()._recalculateAndValidateDate();
          }
        },

        removeProduct: (productId) => {
          const { selectedProducts, configureProducts, viewMode } = get();
          const newProducts = selectedProducts.filter((id) => id !== productId);
          console.log("[BookingFlow] Removing product:", productId);
          set({ selectedProducts: newProducts });

          // Remove from configure products if in configure mode
          if (
            viewMode === "configure" &&
            configureProducts.includes(productId)
          ) {
            const newConfigureProducts = configureProducts.filter(
              (id) => id !== productId,
            );
            set({ configureProducts: newConfigureProducts });
          }

          // SIDE EFFECT: Recalculate date
          get()._recalculateAndValidateDate();
          useBookingStore.getState().clearInvitationCode();
          get().syncSession("product removed");
        },

        setSelectedPackageId: (packageId) => {
          console.log("[BookingFlow] Setting package ID:", packageId);
          set({ selectedPackageId: packageId });
        },

        setConfigureProducts: (products) => {
          console.log("[BookingFlow] Setting configure products:", products);
          set({ configureProducts: products });
        },

        // ========================================================================
        // ADD-ON SELECTION ACTIONS
        // ========================================================================

        toggleAddOn: (addOnId) => {
          const {
            selectedAddOns,
            selectedProducts,
            selectedClinic,
            _clinicProductAvailability,
            _products,
            language,
          } = get();
          const isSelected = selectedAddOns.includes(addOnId);

          if (isSelected) {
            const newAddOns = selectedAddOns.filter((id) => id !== addOnId);
            console.log("[BookingFlow] Removing add-on:", addOnId);
            set({ selectedAddOns: newAddOns });
          } else {
            const newAddOns = [...selectedAddOns, addOnId];
            console.log("[BookingFlow] Adding add-on:", addOnId);
            set({ selectedAddOns: newAddOns });

            // Track add_to_cart with full cart snapshot (main product + all add-ons)
            const allCartIds = [...selectedProducts, ...newAddOns];
            const allCartProducts = allCartIds
              .map((id) => _products.find((p) => p.id === id))
              .filter((p): p is NonNullable<typeof p> => Boolean(p));
            const totalValue = allCartProducts.reduce(
              (sum, p) =>
                sum + (p.default_price ? p.default_price.unit_amount / 100 : 0),
              0,
            );
            trackEvent({
              event: "add_to_cart",
              props: {
                // Discriminates add-on toggles from package selection (PostHog add-on funnel)
                cart_action: "addon",
                ecommerce: {
                  items: allCartProducts.map((p) => ({
                    item_id: p.id,
                    item_name: p.name || p.translations?.name || "Unknown",
                    item_category:
                      p.category?.name ||
                      (p.product_type === "package" ? "Package" : "Add-on"),
                    price: p.default_price
                      ? p.default_price.unit_amount / 100
                      : 0,
                    currency: p.default_price?.currency || "CHF",
                    quantity: 1,
                  })),
                  value: totalValue,
                  currency:
                    allCartProducts[0]?.default_price?.currency || "CHF",
                },
              },
            });

            // CHECK: If clinic is selected, verify add-on is compatible
            if (selectedClinic && _clinicProductAvailability.length > 0) {
              const unavailableAddOns = getUnavailableProductsWithReasons(
                selectedClinic,
                newAddOns,
                _clinicProductAvailability,
                _products,
              );

              if (unavailableAddOns.length > 0) {
                console.log(
                  "[BookingFlow] Add-on(s) incompatible with selected clinic:",
                  unavailableAddOns,
                );

                const addOnNames = unavailableAddOns
                  .map((p) => p.name)
                  .join(", ");
                const lng = languageMap[language];
                const message = i18next.t("toast.addonsNotAvailableAtClinic", {
                  lng,
                  names: addOnNames,
                });

                // Auto-deselect clinic since add-ons are incompatible
                set({
                  selectedClinic: null,
                  selectedDate: null,
                  selectedTimeSlots: [],
                });

                get().showToast({
                  message,
                  type: "warning",
                  duration: 6000,
                  position: "bottom-left",
                });

                // Early return to avoid showing another toast about date being invalid
                return;
              }
            }
          }

          // SIDE EFFECT: Recalculate and validate date
          // This is THE critical fix - when add-ons change, we need to:
          // 1. Recalculate first available date
          // 2. Check if current date is still valid
          // 3. Show toast if date became invalid
          // 4. Auto-select new date if needed
          get()._recalculateAndValidateDate();
          useBookingStore.getState().clearInvitationCode();
          get().syncSession("addon toggled");
        },

        setSelectedAddOns: (addOns) => {
          console.log("[BookingFlow] Setting add-ons:", addOns);
          set({ selectedAddOns: addOns });

          // SIDE EFFECT: Recalculate date
          get()._recalculateAndValidateDate();
        },

        // ========================================================================
        // CLINIC SELECTION ACTIONS
        // ========================================================================

        selectClinic: (clinicId) => {
          const { selectedClinic: previousClinic, selectedTimeSlots } = get();
          console.log(
            "[BookingFlow] Selecting clinic:",
            clinicId,
            "previous:",
            previousClinic,
          );

          // When clinic is cleared (e.g. product type switch), also clear stale time slots
          if (!clinicId && selectedTimeSlots.length > 0) {
            console.log(
              "[BookingFlow] Clinic cleared — clearing stale time slots",
            );
            set({ selectedClinic: null, selectedTimeSlots: [] });
          } else {
            set({ selectedClinic: clinicId });
          }

          // SIDE EFFECT: Recalculate and validate date — must run regardless of step
          // since the user may go back to clinic step with a stale date
          if (get().selectedDate) {
            get()._recalculateAndValidateDate();
          }

          // SIDE EFFECT: When clinic changes, validate existing time slots against new clinic's availability
          if (
            previousClinic !== clinicId &&
            get().selectedTimeSlots.length > 0
          ) {
            get()._validateTimeSlotsForClinic();
          }

          // NOTE: Auto-navigation removed - user must click "Continue" button
          // This provides better UX and prevents unexpected navigation
          // Session sync happens on step change, not on selection
        },

        // ========================================================================
        // DATE & TIME SELECTION ACTIONS
        // ========================================================================

        selectDate: (date, options) => {
          const isManual = options?.isManual ?? true; // Default to manual selection
          console.log(
            "[BookingFlow] Selecting date:",
            date,
            "isManual:",
            isManual,
          );

          if (!date) {
            set({
              selectedDate: null,
              wasDateManuallySelected: false,
              selectedTimeSlots: [],
            });
            return;
          }

          // GUARD: Check if date is valid
          const firstAvailableDate = get().getFirstAvailableDate();
          const dateStart = startOfDay(date);
          const firstAvailableStart = startOfDay(firstAvailableDate);

          if (isBefore(dateStart, firstAvailableStart)) {
            console.log(
              "[BookingFlow] Date is before first available date, rejecting",
            );
            const { language } = get();
            const locale =
              language === "de-DE"
                ? de
                : language === "fr-FR"
                  ? fr
                  : language === "nl-NL"
                    ? nl
                    : enUS;
            const lng = languageMap[language];
            const dateStr = format(date, "PPP", { locale });
            const availableStr = format(firstAvailableDate, "PPP", { locale });

            get().showToast({
              message: i18next.t("toast.dateNotAvailable", {
                lng,
                date: dateStr,
                availableDate: availableStr,
              }),
              type: "error",
              duration: 5000,
            });
            return;
          }

          // UPDATE: Set date and track if manually selected
          const prevDate = get().selectedDate;
          const prevWasManual = get().wasDateManuallySelected;
          // Once manually selected, keep it as manual unless explicitly auto-selecting
          const newWasManual = isManual || prevWasManual;
          set({ selectedDate: date, wasDateManuallySelected: newWasManual });

          // NOTE: We intentionally do NOT clear time slots when the date changes.
          // The time selection handler (selectTimeSlot with previousSlot) handles
          // replacing the first slot when the user picks a new time on the new date.
          // Clearing slots here would shift the second slot's index, breaking the
          // preference system (index 0 = first, index 1 = second).

          // SIDE EFFECT: Track date selection
          trackEvent({
            event: "date_select",
            props: {
              selected_date: date.toISOString(),
              clinic: get().selectedClinic,
            },
          });

          // Session sync happens on step change, not on selection
        },

        selectTimeSlot: (times, options) => {
          const { selectedTimeSlots, selectedDate } = get();
          console.log("[BookingFlow] Selecting time slot:", times, options);

          const effectiveDate = options.dateOverride ?? selectedDate;
          if (!effectiveDate) {
            console.log(
              "[BookingFlow] No date selected, cannot select time slot",
            );
            return;
          }

          // Determine if this is an update or new slot
          const isUpdate = !!options.previousSlot;
          const slotIndex = isUpdate
            ? selectedTimeSlots.findIndex(
                (s) =>
                  s.time === options.previousSlot!.time &&
                  isSameDay(s.date, options.previousSlot!.date),
              )
            : options.preference === "first"
              ? 0
              : 1;

          // Create new slot
          const newSlot: TimeSlot = {
            date: effectiveDate,
            time: times[0], // Use first time from array
            preference: options.preference,
            isFromExternalSlot: options.isFromExternalSlot,
          };

          // Update slots array
          let newSlots = [...selectedTimeSlots];
          if (isUpdate && slotIndex !== -1) {
            // Update existing slot
            newSlots[slotIndex] = newSlot;
          } else {
            // Add new slot
            if (options.preference === "first") {
              newSlots[0] = newSlot;
            } else {
              newSlots[1] = newSlot;
            }
          }

          // Filter out undefined slots
          newSlots = newSlots.filter((s) => s !== undefined);

          console.log("[BookingFlow] New time slots:", newSlots);
          set({ selectedTimeSlots: newSlots });

          // SIDE EFFECT: Track time selection
          trackEvent({
            event: "time_slot_select",
            props: {
              time: times[0],
              preference: options.preference,
              clinic: get().selectedClinic,
            },
          });

          // Session sync happens on step change, not on selection
        },

        deleteTimeSlot: (slot) => {
          const { selectedTimeSlots, currentStep } = get();
          console.log("[BookingFlow] Deleting time slot:", slot);

          const newSlots = selectedTimeSlots.filter(
            (s) => !(s.time === slot.time && isSameDay(s.date, slot.date)),
          );

          set({ selectedTimeSlots: newSlots });

          // SIDE EFFECT: If we're on details step and have no slots left, go back to date
          if (currentStep === "details" && newSlots.length === 0) {
            setTimeout(() => {
              get().goToStep("date", { skipValidation: true });
            }, 50);
          }

          // Session sync happens on step change (goToStep will be called above if needed)
        },

        setSecondSlotCalendarDate: (date) => {
          console.log("[BookingFlow] Setting second slot calendar date:", date);
          set({ secondSlotCalendarDate: date });
        },

        setBookingType: (type) => {
          console.log("[BookingFlow] Setting booking type:", type);
          set({ bookingType: type });
        },

        // ========================================================================
        // BOOKING MODE ACTIONS
        // ========================================================================

        setBookingMode: (mode) => {
          console.log("[BookingFlow] Setting booking mode:", mode);
          set({ bookingMode: mode });
        },

        // ========================================================================
        // FORM SUBMISSION ACTIONS
        // ========================================================================

        setSubmitting: (value) => set({ isSubmitting: value }),
        setSubmitted: (value) => set({ isSubmitted: value }),

        updateFormData: (data) => {
          console.log("[BookingFlow] Updating form data");
          set({ formData: data });
          // Session sync happens on step change, not on form updates
        },

        submitBooking: async (data) => {
          console.log("[BookingFlow] Submitting booking");
          // NOTE: Do NOT set isLoading here — that flag is used at the App level
          // for session restoration and causes the skeleton to flash.
          // Submission state is tracked via isSubmitting/isSubmitted.
          set({ formData: data });

          try {
            // Navigate to confirmation page (confetti page) on successful submission
            get().goToStep("confirmation");
          } catch (error) {
            console.error("[BookingFlow] Booking submission failed:", error);
            get().showToast({
              message: i18next.t("toast.submitFailed", {
                lng: languageMap[get().language],
              }),
              type: "error",
              duration: 5000,
            });
          }
        },

        setBookingId: (bookingId) => {
          console.log("[BookingFlow] Setting booking ID:", bookingId);
          set({ bookingId });
        },

        getOrCreateBookingId: () => {
          const existing = get().bookingId;
          if (existing) return existing;
          const id = generateSessionId();
          set({ bookingId: id });
          return id;
        },

        clearFormData: () => {
          console.log("[BookingFlow] Clearing form data from store");
          set({ formData: null });
        },

        setSessionId: (id) => {
          console.log("[BookingFlow] Setting session ID:", id);
          set({ sessionId: id });
          if (id) {
            setSessionIdCookie(id);
          }
        },

        /**
         * Create a state snapshot for recording to the server
         */
        createStateSnapshot: (): SessionStateSnapshot => {
          const state = get();
          return {
            selectedProducts: state.selectedProducts,
            selectedAddOns: state.selectedAddOns,
            selectedPackageId: state.selectedPackageId,
            selectedPackageType: state.selectedPackageType,
            viewMode: state.viewMode,
            selectedClinic: state.selectedClinic,
            selectedDate: state.selectedDate?.toISOString() || null,
            wasDateManuallySelected: state.wasDateManuallySelected,
            selectedTimeSlots: state.selectedTimeSlots.map((slot) => ({
              date: slot.date.toISOString(),
              time: slot.time,
              isFastBooking: slot.isFastBooking,
            })),
            formData: state.formData as SessionFormData | null,
            // Generate + persist the booking id with the snapshot so it exists
            // well before payment and survives a TWINT redirect's page reload
            // (loadSession restores it → dedupe with the webhook).
            bookingId: get().getOrCreateBookingId(),
            membershipPlanActive: useMembershipPlanStore.getState().active,
            membershipFirst: useMembershipPlanStore.getState().membershipFirst,
            bundleAttached: useMembershipPlanStore.getState().bundleAttached,
            membershipPlan: useMembershipPlanStore.getState().plan,
            // Details-page payment choice + the membership-checkout marker
            // (see sessionState.ts for the semantics of each — only the
            // marker classifies membership purchases; the mode is context).
            selectedPaymentMode: state.selectedPaymentMode,
            membershipCheckoutInitiated: state.membershipCheckoutInitiated,
            market: state.market,
            language: state.language,
            bookingType: state.bookingType,
          };
        },

        /**
         * Record current state to server via edge function
         * Called on each step change - this is the server-first state model
         * Uses exponential backoff to prevent rapid-fire calls
         */
        syncSession: async (reason = "unknown") => {
          const { currentStep, isLoading } = get();

          const now = Date.now();
          const timeSinceLastSync = now - lastSyncTime;
          const requiredWaitTime = Math.max(
            MIN_SYNC_INTERVAL_MS,
            syncBackoffMs,
          );

          // Log all sync attempts for debugging
          console.log(
            `[BookingFlow] syncSession called - reason: "${reason}", step: ${currentStep}, timeSinceLastSync: ${timeSinceLastSync}ms, requiredWait: ${requiredWaitTime}ms`,
          );

          // Skip sync if we're currently loading session from server
          // or haven't finished initial session load yet (prevents overwriting
          // server state with empty defaults during rehydration)
          if (isLoading) {
            console.log(
              `[BookingFlow] syncSession SKIPPED (reason: "${reason}") - currently loading`,
            );
            return;
          }

          // Skip if loadSession redirected due to validation — don't overwrite
          // the server's good state with the fallback/redirected state
          if (get()._skipNextSync) {
            console.log(
              `[BookingFlow] syncSession SKIPPED (reason: "${reason}") - redirect recovery, preserving server state`,
            );
            set({ _skipNextSync: false });
            return;
          }
          if (!get().hasInitializedFromUrl && get().sessionId) {
            console.log(
              `[BookingFlow] syncSession SKIPPED (reason: "${reason}") - session exists but not yet loaded from server`,
            );
            return;
          }

          // Throttle: skip if called too soon
          // Temporarily disabled - remove comment below to re-enable throttling
          // eslint-disable-next-line no-constant-condition, no-constant-binary-expression
          if (false && timeSinceLastSync < requiredWaitTime) {
            // Increase backoff for next attempt
            syncBackoffMs = Math.min(
              syncBackoffMs === 0
                ? MIN_SYNC_INTERVAL_MS
                : syncBackoffMs * BACKOFF_MULTIPLIER,
              MAX_BACKOFF_MS,
            );
            console.log(
              `[BookingFlow] syncSession THROTTLED (reason: "${reason}") - ` +
                `wait ${requiredWaitTime - timeSinceLastSync}ms, backoff now ${syncBackoffMs}ms`,
            );
            return;
          }

          // Reset backoff on successful sync attempt
          syncBackoffMs = 0;
          lastSyncTime = now;

          await get().recordStep(currentStep, reason);
        },

        recordStep: async (step, reason, extra) => {
          const { language, sessionFlow } = get();
          let { sessionId } = get();

          // If no sessionId, generate one and persist it
          if (!sessionId) {
            // Check cookie first
            sessionId = getSessionIdFromCookie();
            if (!sessionId) {
              sessionId = generateSessionId();
            }
            set({ sessionId });
            setSessionIdCookie(sessionId);
            console.log("[BookingFlow] Generated new session ID:", sessionId);
          }

          // Create state snapshot, with what the caller's own store adds
          const stateSnapshot = { ...get().createStateSnapshot(), ...extra };

          // Capture URL params
          const urlParams = captureUrlParams();
          const fullUrl = getFullUrl();

          // Determine if this is a completion (confirmation step)
          const isCompleted = step === "confirmation";

          // Record state entry via edge function
          const result = await sessionStateService.recordStateTransition({
            session_id: sessionId,
            step_name: step,
            full_url: fullUrl,
            url_params: urlParams,
            state: stateSnapshot,
            is_completed: isCompleted,
            language,
            ...(sessionFlow ? { flow: sessionFlow } : {}),
          });

          if (result.success) {
            console.log(
              `[BookingFlow] State recorded (reason: "${reason}"):`,
              result.entry_id,
            );
            // Send to PostHog. Captured directly instead of via trackEvent:
            // the snapshot carries formData (PII) which must not be pushed to
            // the GTM dataLayer. The suppression guard still has to apply —
            // synthetic test sessions (testUserId=) otherwise emit booking_step
            // while booking_complete stays guarded, inflating every funnel
            // built on this event.
            if (!isAnalyticsSuppressed()) {
              posthog.capture("booking_step", {
                step_name: step,
                session_id: sessionId,
                is_completed: isCompleted,
                reason,
                // The snapshot's formData carries name/email/phone/birthday/
                // address/notes — stripped here, kept in the server-side
                // session record. AHD-9080.
                ...stripPiiForPostHog(stateSnapshot),
                // Preserve the funnel signal without the contents.
                form_data_filled: Boolean(stateSnapshot.formData),
              });
            }
          } else {
            console.error(
              `[BookingFlow] Failed to record state (reason: "${reason}"):`,
              result.message,
            );
          }
        },

        /**
         * Load session state from the latest server entry
         */
        loadSession: async (id: string) => {
          // Guard against multiple concurrent loads
          const { isLoading, hasInitializedFromUrl } = get();
          if (isLoading || hasInitializedFromUrl) {
            console.log(
              "[BookingFlow] loadSession skipped - already loading or initialized",
            );
            return;
          }

          try {
            set({ isLoading: true });

            // Get the latest state entry for this session
            const latestEntry =
              await sessionStateService.getLatestStateEntry(id);

            if (!latestEntry) {
              console.log("[BookingFlow] No session entries found for ID:", id);
              // Session ID exists in localStorage but has no server state
              // (e.g. user navigated away before sync completed).
              // Check URL directly too — initializeFromUrl may not have run yet
              // if products haven't loaded, so _conflictUrlProducts may be empty
              // even though the URL still carries ?products=.
              const stashedUrlProducts = get()._conflictUrlProducts;
              const directUrlProducts = getProductIdsFromUrl();
              const pendingProducts =
                directUrlProducts.length > 0
                  ? directUrlProducts
                  : stashedUrlProducts;

              clearSessionIdCookie();
              set({
                sessionId: null,
                isLoading: false,
                hasInitializedFromUrl: true,
              });

              if (pendingProducts.length > 0) {
                // Clear URL now that we've captured the IDs
                if (directUrlProducts.length > 0) {
                  updateUrlWithBookingParams({ products: "" });
                }

                console.log(
                  "[BookingFlow] Processing URL products after empty/missing session:",
                  pendingProducts,
                );
                const { _products } = get();

                if (_products.length === 0) {
                  // Products haven't loaded yet — defer to avoid the "mri" fallback.
                  console.log(
                    "[BookingFlow] Products not loaded yet, deferring URL product apply:",
                    pendingProducts,
                  );
                  set({
                    _conflictUrlProducts: pendingProducts,
                    _pendingEmptyUrlApply: true,
                  });
                } else {
                  const urlPackages = pendingProducts.filter((id) => {
                    const p = _products.find((prod) => prod.id === id);
                    return !p || p.product_type === "package";
                  });
                  const urlAddOns = pendingProducts.filter((id) => {
                    const p = _products.find((prod) => prod.id === id);
                    return p && p.product_type !== "package";
                  });
                  const urlPackage = _products.find(
                    (p) =>
                      urlPackages.includes(p.id) &&
                      p.product_type === "package",
                  );

                  set({
                    _conflictUrlProducts: [],
                    selectedProducts: urlPackages,
                    selectedAddOns: urlAddOns,
                    selectedPackageId: urlPackage?.id || null,
                    selectedPackageType:
                      urlPackage?.package_types?.[0] || "mri",
                    viewMode: urlPackage ? "packages" : "configure",
                    currentStep: "product",
                  });
                  // Open the step the link asked for, as a fresh visit does
                  // (Eugene, 22.10): the one-off handoff's add-ons, the quiz's
                  // clinic. The deferred apply does it in setProducts.
                  get()._openCartEntryStep(!!urlPackage);
                }
              }
              return;
            }

            console.log(
              "[BookingFlow] Session loaded from server:",
              id,
              "step:",
              latestEntry.step_name,
            );

            // Rehydrate dates in state - convert string dates to Date objects
            const state = latestEntry.state;
            console.log(
              "[BookingFlow] loadSession: Raw state from server:",
              JSON.stringify({
                selectedDate: state.selectedDate,
                selectedDateType: typeof state.selectedDate,
                selectedTimeSlots: state.selectedTimeSlots?.map((s) => ({
                  date: s.date,
                  dateType: typeof s.date,
                  time: s.time,
                })),
              }),
            );

            // Safely parse selectedDate
            let parsedDate: Date | null = null;
            if (state.selectedDate) {
              const d = new Date(state.selectedDate);
              if (!isNaN(d.getTime())) {
                parsedDate = d;
                console.log(
                  "[BookingFlow] loadSession: Parsed selectedDate:",
                  state.selectedDate,
                  "→",
                  parsedDate,
                );
              } else {
                console.warn(
                  "[BookingFlow] loadSession: Failed to parse selectedDate:",
                  state.selectedDate,
                );
              }
            }

            // Safely parse time slots - convert string dates to Date objects
            const parsedTimeSlots: TimeSlot[] = [];
            for (const slot of state.selectedTimeSlots || []) {
              if (!slot.date) {
                console.warn(
                  "[BookingFlow] loadSession: Invalid time slot (missing date), skipping slot",
                );
                continue;
              }
              const slotDate = new Date(slot.date);
              if (isNaN(slotDate.getTime())) {
                console.warn(
                  "[BookingFlow] loadSession: Invalid time slot date, skipping slot:",
                  slot.date,
                );
                continue;
              }
              console.log(
                "[BookingFlow] loadSession: Parsed time slot date:",
                slot.date,
                "→",
                slotDate,
                "instanceof Date:",
                slotDate instanceof Date,
              );
              parsedTimeSlots.push({
                ...slot,
                date: slotDate,
              });
            }

            console.log(
              "[BookingFlow] loadSession: Final parsed state:",
              JSON.stringify({
                selectedDate: parsedDate?.toISOString(),
                selectedDateInstanceofDate: parsedDate instanceof Date,
                selectedTimeSlots: parsedTimeSlots.map((s) => ({
                  date: s.date.toISOString(),
                  dateInstanceofDate: s.date instanceof Date,
                  time: s.time,
                })),
              }),
            );

            const restoredCart = {
              selectedProducts: state.selectedProducts || [],
              selectedAddOns: state.selectedAddOns || [],
            };
            const rehydratedState: Partial<BookingFlowState> = {
              ...(dropUnavailableCartItems(
                restoredCart.selectedProducts,
                restoredCart.selectedAddOns,
                get()._products,
              ) ?? restoredCart),
              selectedPackageId: state.selectedPackageId || null,
              selectedPackageType: state.selectedPackageType || "mri",
              viewMode: state.viewMode || "packages",
              selectedClinic: state.selectedClinic || null,
              selectedDate: parsedDate,
              wasDateManuallySelected: state.wasDateManuallySelected ?? false,
              selectedTimeSlots: parsedTimeSlots,
              formData: (state.formData as FormData | null) || null,
              // Restore the SAME booking id so a TWINT-return browser submit
              // dedupes with the webhook (idempotency_key) instead of double-booking.
              bookingId: state.bookingId ?? get().bookingId,
              // Restore the membership-checkout marker with the same
              // TWINT-return rationale: it was synced right before the
              // redirect, and the confirmation booking_step after the return
              // must still classify the purchase as a membership.
              // (selectedPaymentMode is deliberately NOT restored — the
              // details form's compliance effect re-nulls it on mount, and
              // restoring it would risk a pre-ticked paid option.)
              membershipCheckoutInitiated:
                state.membershipCheckoutInitiated ?? null,
              language: (() => {
                const urlLocale = new URLSearchParams(
                  window.location.search,
                ).get("locale");
                const urlLanguage: BookingLanguage | null =
                  urlLocale === "de" || urlLocale === "de-DE"
                    ? "de-DE"
                    : urlLocale === "fr" || urlLocale === "fr-FR"
                      ? "fr-FR"
                      : urlLocale === "nl" || urlLocale === "nl-NL"
                        ? "nl-NL"
                        : urlLocale === "en" || urlLocale === "en-US"
                          ? "en-US"
                          : null;
                return urlLanguage || state.language || get().language;
              })(),
              bookingType: state.bookingType || "request",
            };

            const serverStep = latestEntry.step_name as BookingStep;

            // If session has a clinic, fetch external slots before showing UI
            if (rehydratedState.selectedClinic) {
              console.log(
                "[BookingFlow] loadSession: Fetching external slots for clinic:",
                rehydratedState.selectedClinic,
              );
              const externalSlots = await fetchExternalSlotsForClinic(
                rehydratedState.selectedClinic,
              );
              console.log(
                "[BookingFlow] loadSession: External slots loaded, count:",
                externalSlots.length,
              );

              // Store the external slots
              const currentExternalSlots = get()._clinicExternalSlots;
              set({
                _clinicExternalSlots: {
                  ...currentExternalSlots,
                  [rehydratedState.selectedClinic]: externalSlots,
                },
              });
            }

            // Reconstruct stepHistory in the ACTIVE funnel order so goBack()
            // works after restore. The membership flow inserts a step (cadence),
            // so derive the order from the PERSISTED membership flag — the global
            // membership store is restored later (it must stay late so
            // ProductStep's transient mount during init can't clear it first).
            // The one-off handoff drops that flag below, so its order is the
            // standard one.
            const restoredStepOrder =
              !isOneTimeHandoff() && (state.membershipFirst ?? false)
                ? MEMBERSHIP_STEP_ORDER
                : STANDARD_STEP_ORDER;
            const serverStepIndex = restoredStepOrder.indexOf(serverStep);
            const reconstructedHistory =
              serverStepIndex > 0
                ? (restoredStepOrder.slice(0, serverStepIndex) as BookingStep[])
                : [];
            console.log(
              "[BookingFlow] loadSession: Reconstructed stepHistory:",
              reconstructedHistory,
            );

            // Use the URL step if present, otherwise default to product.
            // This lets the user stay on their current step after refresh
            // while the server data (products, clinic, etc.) is restored.
            // No ?step= in URL means fresh entry → always start at product.
            const urlStep = getStepFromUrl() as BookingStep | null;

            // Return from a Stripe REDIRECT payment (TWINT/Klarna/PayPal): the
            // browser full-page-reloads back here. This is the single handler for
            // that return — route straight to confirmation regardless of what step
            // the URL/rehydration would otherwise pick (the empty-products bounce
            // below or a ?code= campaign re-hijack would otherwise strand the user
            // on product with un-stripped Stripe params). The booking is created
            // async by the invoice.paid webhook; formData is restored from
            // localStorage just below so the confirmation email block still renders.
            const returnParams = new URLSearchParams(window.location.search);
            const isPaymentReturn = returnParams.get("payment_return") === "1";
            // A retry leaves the previous attempt's params behind (the return_url
            // is built from window.location.href), so the same key can appear
            // twice — e.g. redirect_status=failed THEN =succeeded. get() returns
            // the FIRST (stale "failed"), so check ALL values: any "succeeded"
            // means the payment ultimately went through.
            const paymentReturnSuccess =
              isPaymentReturn &&
              returnParams.getAll("redirect_status").includes("succeeded");
            if (isPaymentReturn) {
              // Strip the Stripe params on ANY return (success or fail) so a
              // subsequent retry can't accumulate them into duplicates.
              const u = new URL(window.location.href);
              [
                "payment_return",
                "redirect_status",
                "payment_intent",
                "payment_intent_client_secret",
              ].forEach((k) => u.searchParams.delete(k));
              window.history.replaceState({}, "", u.toString());
            }
            if (paymentReturnSuccess) {
              // The session snapshot's formData is null (store.formData is only set
              // by submitBooking, at the very end), so the confirmation page's
              // "check your inbox" block would have no email. Restore the values the
              // user typed from localStorage — BookingForm persists them there as
              // they type, and they survive the redirect round-trip.
              const savedForm = safeLocalStorage.getItem("bookingFormData");
              if (savedForm) {
                try {
                  rehydratedState.formData = JSON.parse(savedForm) as FormData;
                } catch (e) {
                  console.warn(
                    "[BookingFlow] could not restore form data for confirmation:",
                    e,
                  );
                }
              }
            }

            const targetStep = paymentReturnSuccess
              ? ("confirmation" as BookingStep)
              : urlStep && restoredStepOrder.includes(urlStep)
                ? urlStep
                : ("product" as BookingStep);

            const targetStepIndex = restoredStepOrder.indexOf(targetStep);
            const targetHistory =
              targetStepIndex > 0
                ? (restoredStepOrder.slice(0, targetStepIndex) as BookingStep[])
                : [];

            console.log(
              "[BookingFlow] loadSession: Using step:",
              targetStep,
              urlStep ? "(from URL)" : "(from server)",
            );

            set({
              sessionId: id,
              currentStep: targetStep,
              stepHistory: targetHistory,
              ...rehydratedState,
              hasInitializedFromUrl: true,
            });

            // Restore the membership cadence flag last — the product step mounts
            // transiently during init and clears it (ProductStep), so this async
            // restore (after we've left the product step) is the final authority.
            const planStore = useMembershipPlanStore.getState();
            planStore.setActive(state.membershipPlanActive ?? false);
            planStore.setMembershipFirst(state.membershipFirst ?? false);
            planStore.setBundleAttached(state.bundleAttached ?? false);
            if (state.membershipPlan) {
              planStore.setPlan(state.membershipPlan as RecommendedPlan);
            }
            // …except on the one-off handoff, where that restore would sell
            // the "once" pick as a membership or a bundle: ProductStep, which
            // would reset it, never mounts on the way to the add-ons.
            get()._dropMembershipOnOneTimeHandoff();

            // Validate: if critical data is missing for the target step,
            // redirect to the earliest step that needs it.
            const restoredProducts = rehydratedState.selectedProducts || [];
            const restoredClinic = rehydratedState.selectedClinic;
            const hasDate = parsedDate !== null || parsedTimeSlots.length > 0;

            const stepIndex = restoredStepOrder.indexOf(targetStep);
            const dateIndex = restoredStepOrder.indexOf("date");
            const detailsIndex = restoredStepOrder.indexOf("details");

            let redirectStep: BookingStep | null = null;

            // A successful payment return goes to confirmation unconditionally —
            // never bounce it back into the funnel for "missing" funnel data.
            if (!paymentReturnSuccess) {
              if (restoredProducts.length === 0 && stepIndex > 0) {
                redirectStep = "product";
              } else if (!restoredClinic && stepIndex >= dateIndex) {
                redirectStep = "clinic";
              } else if (!hasDate && stepIndex >= detailsIndex) {
                redirectStep = "date";
              }
            }

            if (redirectStep) {
              const redirectIndex = restoredStepOrder.indexOf(redirectStep);
              const redirectHistory =
                redirectIndex > 0
                  ? (restoredStepOrder.slice(0, redirectIndex) as BookingStep[])
                  : [];
              console.log(
                `[BookingFlow] loadSession: Invalid state at step "${targetStep}", redirecting to "${redirectStep}"`,
              );
              set({
                currentStep: redirectStep,
                stepHistory: redirectHistory,
                _skipNextSync: true, // Don't overwrite server state with fallback
              });
              replaceStepInUrl(redirectStep);
            } else {
              replaceStepInUrl(targetStep);
            }

            // Persist session ID to cookie
            setSessionIdCookie(id);

            // Validate persisted date - if date is in the past, redirect to date step
            // This runs regardless of products to handle edge cases like session with stale dates
            if (
              !paymentReturnSuccess &&
              (rehydratedState.selectedDate || parsedTimeSlots.length > 0)
            ) {
              const selectedDateStart = rehydratedState.selectedDate
                ? startOfDay(rehydratedState.selectedDate)
                : parsedTimeSlots.length > 0
                  ? startOfDay(parsedTimeSlots[0].date)
                  : null;

              if (selectedDateStart) {
                const today = startOfDay(new Date());
                const isInPast = isBefore(selectedDateStart, today);

                if (isInPast) {
                  const wasManual =
                    rehydratedState.wasDateManuallySelected ?? false;
                  console.log(
                    "[BookingFlow] loadSession: Date is in the past, redirecting to date step",
                    {
                      selectedDate: selectedDateStart,
                      today,
                      wasManuallySelected: wasManual,
                    },
                  );

                  trackEvent({
                    event: "persisted_selection_invalidated",
                    props: {
                      kind: "date_past_on_load",
                      was_manual: wasManual,
                      step_when_fired: get().currentStep,
                    },
                  });

                  // Clear invalid date and time slots
                  set({
                    selectedDate: null,
                    wasDateManuallySelected: false,
                    selectedTimeSlots: [],
                    currentStep: "date",
                    stepHistory: restoredStepOrder.slice(
                      0,
                      restoredStepOrder.indexOf("date"),
                    ) as BookingStep[],
                  });
                  replaceStepInUrl("date");

                  // Only show toast if user actively selected the date
                  // Showing a toast about a date the user never chose is confusing
                  if (wasManual) {
                    const locale =
                      get().language === "de-DE"
                        ? de
                        : get().language === "fr-FR"
                          ? fr
                          : get().language === "nl-NL"
                            ? nl
                            : enUS;
                    const dateStr = format(selectedDateStart, "PPP", {
                      locale,
                    });
                    get().showToast({
                      message: i18next.t(
                        "toast.previouslySelectedDateNoLongerAvailable",
                        {
                          lng: languageMap[get().language],
                          date: dateStr,
                        },
                      ),
                      type: "warning",
                      duration: 5000,
                      position: "bottom-left",
                    });
                  }

                  set({ isLoading: false });
                  return;
                }
              }
            }

            // Additional validation: if we have products, check against first available date
            if (
              rehydratedState.selectedProducts &&
              rehydratedState.selectedProducts.length > 0
            ) {
              get()._validatePersistedDate();
            }

            // Check for URL products that conflict with restored session
            // Get from URL directly (if initializeFromUrl hasn't run yet) or from stashed state
            const urlProductIds = getProductIdsFromUrl();
            const stashedUrlProducts = get()._conflictUrlProducts;
            const conflictCandidates =
              urlProductIds.length > 0 ? urlProductIds : stashedUrlProducts;

            if (conflictCandidates.length > 0) {
              const sessionProducts = get().selectedProducts;
              const hasNewProducts = conflictCandidates.some(
                (id) => !sessionProducts.includes(id),
              );
              if (hasNewProducts && sessionProducts.length > 0) {
                // Only show conflict dialog if the session actually has products
                console.log(
                  "[BookingFlow] loadSession: Conflict detected with URL products",
                  { sessionProducts, conflictCandidates },
                );
                set({
                  pendingSessionConflict: true,
                  _conflictSessionProducts: sessionProducts,
                  _conflictSessionAddOns: get().selectedAddOns,
                  _conflictUrlProducts: conflictCandidates,
                });
              } else if (hasNewProducts && sessionProducts.length === 0) {
                // Session is empty — apply URL products directly, no conflict
                const { _products } = get();

                if (_products.length === 0) {
                  // Products haven't loaded yet. Deferring avoids falling back
                  // to the default "mri" package type for a blood-only package,
                  // which would otherwise leak MRI-only clinics into the flow.
                  // setProducts will complete the apply once products arrive.
                  console.log(
                    "[BookingFlow] loadSession: Empty session, products not loaded yet — deferring URL product apply:",
                    conflictCandidates,
                  );
                  clearSessionIdCookie();
                  set({
                    _conflictUrlProducts: conflictCandidates,
                    _pendingEmptyUrlApply: true,
                    sessionId: null,
                  });
                } else {
                  console.log(
                    "[BookingFlow] loadSession: Empty session, applying URL products directly",
                    conflictCandidates,
                  );
                  const urlPackages = conflictCandidates.filter((id) => {
                    const p = _products.find((prod) => prod.id === id);
                    return !p || p.product_type === "package";
                  });
                  const urlAddOns = conflictCandidates.filter((id) => {
                    const p = _products.find((prod) => prod.id === id);
                    return p && p.product_type !== "package";
                  });
                  const urlPackage = _products.find(
                    (p) =>
                      urlPackages.includes(p.id) &&
                      p.product_type === "package",
                  );

                  clearSessionIdCookie();
                  set({
                    _conflictUrlProducts: [],
                    selectedProducts: urlPackages,
                    selectedAddOns: urlAddOns,
                    selectedPackageId: urlPackage?.id || null,
                    selectedPackageType:
                      urlPackage?.package_types?.[0] || "mri",
                    viewMode: urlPackage ? "packages" : "configure",
                    sessionId: null,
                  });
                  // Open the step the link asked for, as a fresh visit does
                  // (Eugene, 22.10), not the product step the empty cart's
                  // validation above fell back to. The deferred apply does it
                  // in setProducts.
                  get()._openCartEntryStep(!!urlPackage);
                }
              } else {
                // URL products are subset of session → no conflict, just clear stashed state
                // But still ensure viewMode matches the URL package type
                console.log(
                  "[BookingFlow] loadSession: URL products are subset of session, continuing",
                );
                const { _products: allProducts } = get();
                const urlPackage = allProducts.find(
                  (p) =>
                    conflictCandidates.includes(p.id) &&
                    p.product_type === "package",
                );
                const updates: Record<string, unknown> = {
                  _conflictUrlProducts: [],
                };
                if (urlPackage) {
                  updates.selectedPackageType =
                    urlPackage.package_types?.[0] || "mri";
                  updates.selectedPackageId = urlPackage.id;
                  updates.viewMode = "packages";
                }
                set(updates as never);
              }
              // Clear URL products param
              if (urlProductIds.length > 0) {
                updateUrlWithBookingParams({ products: "" });
              }
            }
          } catch (err) {
            console.error("[BookingFlow] Failed to load session:", err);
          } finally {
            set({ isLoading: false });
          }
        },

        /**
         * Refresh state from server on tab focus (bypasses initialization guards)
         * Used to sync state when user switches back to a tab
         */
        refreshFromServer: async () => {
          const { sessionId, isLoading } = get();

          // Skip if no session or already loading
          if (!sessionId || isLoading) return;

          // Capture step before async fetch to detect user navigation during fetch
          const stepBeforeFetch = get().currentStep;

          try {
            const latestEntry =
              await sessionStateService.getLatestStateEntry(sessionId);

            if (!latestEntry) return;

            // The entry belongs to the session we asked about. If another one
            // took over during the fetch, drop it: the click that resolves the
            // session-conflict dialog also focuses the tab, so the refresh it
            // starts for the old session lands after "use new selection" and
            // put the old cart back over the link's.
            if (get().sessionId !== sessionId) {
              console.log(
                "[BookingFlow] refreshFromServer: Session changed during fetch, aborting refresh",
                "before:",
                sessionId,
                "after:",
                get().sessionId,
              );
              return;
            }

            // If user navigated during the fetch (e.g., clicked back),
            // abort refresh to avoid overwriting their navigation
            const stepAfterFetch = get().currentStep;
            if (stepAfterFetch !== stepBeforeFetch) {
              console.log(
                "[BookingFlow] refreshFromServer: User navigated during fetch, aborting refresh",
                "before:",
                stepBeforeFetch,
                "after:",
                stepAfterFetch,
              );
              return;
            }

            // Only update if server has newer data (different step or state)
            const currentStep = stepAfterFetch;
            const serverStep = latestEntry.step_name as BookingStep;

            // Get step indices to compare progress (active funnel, so the
            // membership "cadence" step isn't a -1 that breaks the comparison)
            const compareOrder = getActiveStepOrder();
            const currentStepIndex = compareOrder.indexOf(currentStep);
            const serverStepIndex = compareOrder.indexOf(serverStep);

            // Skip if local state is ahead of server state
            // This prevents overwriting local progress with stale server data
            if (currentStepIndex > serverStepIndex) {
              console.log(
                "[BookingFlow] refreshFromServer: Local state is ahead, skipping refresh",
                "local:",
                currentStep,
                "server:",
                serverStep,
              );
              return;
            }

            if (serverStep === currentStep) {
              // Same step - check if local state has more data than server
              // If local has selections that server doesn't, skip refresh to avoid data loss
              const serverProducts = latestEntry.state.selectedProducts || [];
              const localProducts = get().selectedProducts;
              const serverAddOns = latestEntry.state.selectedAddOns || [];
              const localAddOns = get().selectedAddOns;
              const serverClinic = latestEntry.state.selectedClinic;
              const localClinic = get().selectedClinic;

              // Skip if local has more data (user made selections not yet synced)
              if (
                localProducts.length > serverProducts.length ||
                localAddOns.length > serverAddOns.length ||
                (localClinic && !serverClinic)
              ) {
                console.log(
                  "[BookingFlow] refreshFromServer: Local state has more data, skipping refresh",
                  {
                    localProducts: localProducts.length,
                    serverProducts: serverProducts.length,
                    localAddOns: localAddOns.length,
                    serverAddOns: serverAddOns.length,
                    localClinic,
                    serverClinic,
                  },
                );
                return;
              }

              // Skip if state is identical
              if (
                JSON.stringify(serverProducts) ===
                  JSON.stringify(localProducts) &&
                JSON.stringify(serverAddOns) === JSON.stringify(localAddOns) &&
                serverClinic === localClinic
              ) {
                // No significant changes, skip update
                return;
              }
            }

            console.log(
              "[BookingFlow] Refreshing from server on tab focus:",
              sessionId,
              "server step:",
              latestEntry.step_name,
            );

            // Rehydrate state from server - convert string dates to Date objects
            const state = latestEntry.state;
            console.log(
              "[BookingFlow] refreshFromServer: Raw state from server:",
              JSON.stringify({
                selectedDate: state.selectedDate,
                selectedDateType: typeof state.selectedDate,
                selectedTimeSlots: state.selectedTimeSlots?.map((s) => ({
                  date: s.date,
                  dateType: typeof s.date,
                  time: s.time,
                })),
              }),
            );

            // Safely parse date - ensure it's a valid Date object
            let parsedDate: Date | null = null;
            if (state.selectedDate) {
              const d = new Date(state.selectedDate);
              if (!isNaN(d.getTime())) {
                parsedDate = d;
                console.log(
                  "[BookingFlow] refreshFromServer: Parsed selectedDate:",
                  state.selectedDate,
                  "→",
                  parsedDate,
                );
              } else {
                console.warn(
                  "[BookingFlow] refreshFromServer: Failed to parse selectedDate:",
                  state.selectedDate,
                );
              }
            }

            // Safely parse time slots - convert string dates to Date objects
            const parsedTimeSlots: TimeSlot[] = [];
            for (const slot of state.selectedTimeSlots || []) {
              if (!slot.date) {
                console.warn(
                  "[BookingFlow] refreshFromServer: Invalid time slot (missing date), skipping slot",
                );
                continue;
              }
              const slotDate = new Date(slot.date);
              if (isNaN(slotDate.getTime())) {
                console.warn(
                  "[BookingFlow] refreshFromServer: Invalid time slot date, skipping slot:",
                  slot.date,
                );
                continue;
              }
              console.log(
                "[BookingFlow] refreshFromServer: Parsed time slot date:",
                slot.date,
                "→",
                slotDate,
                "instanceof Date:",
                slotDate instanceof Date,
              );
              parsedTimeSlots.push({
                ...slot,
                date: slotDate,
              });
            }

            console.log(
              "[BookingFlow] refreshFromServer: Final parsed state:",
              JSON.stringify({
                selectedDate: parsedDate?.toISOString(),
                selectedDateInstanceofDate: parsedDate instanceof Date,
                selectedTimeSlots: parsedTimeSlots.map((s) => ({
                  date: s.date.toISOString(),
                  dateInstanceofDate: s.date instanceof Date,
                  time: s.time,
                })),
              }),
            );

            const newStep = latestEntry.step_name as BookingStep;

            const restoredCart = {
              selectedProducts: state.selectedProducts || [],
              selectedAddOns: state.selectedAddOns || [],
            };
            set({
              ...(dropUnavailableCartItems(
                restoredCart.selectedProducts,
                restoredCart.selectedAddOns,
                get()._products,
              ) ?? restoredCart),
              selectedPackageId: state.selectedPackageId || null,
              selectedPackageType: state.selectedPackageType || "mri",
              viewMode: state.viewMode || "packages",
              selectedClinic: state.selectedClinic || null,
              selectedDate: parsedDate,
              wasDateManuallySelected: state.wasDateManuallySelected ?? false,
              selectedTimeSlots: parsedTimeSlots,
              currentStep: newStep,
              // Restore the stable booking id (idempotency anchor) from the server.
              bookingId: state.bookingId ?? get().bookingId,
              // Keep the membership-checkout marker server-authoritative, same
              // as the cadence flag below (it was synced at initiation).
              membershipCheckoutInitiated:
                state.membershipCheckoutInitiated ?? null,
            });

            // Keep the membership cadence flag in sync with the server snapshot.
            const planStore = useMembershipPlanStore.getState();
            planStore.setActive(state.membershipPlanActive ?? false);
            planStore.setMembershipFirst(state.membershipFirst ?? false);
            planStore.setBundleAttached(state.bundleAttached ?? false);
            if (state.membershipPlan) {
              planStore.setPlan(state.membershipPlan as RecommendedPlan);
            }

            // Update URL to match synced step
            replaceStepInUrl(newStep);

            // Validate date if we have products
            if (state.selectedProducts?.length > 0) {
              get()._validatePersistedDate();
            }
          } catch (err) {
            console.error("[BookingFlow] Failed to refresh from server:", err);
            // On any error, reset to clean state
            get().resetFlow();
            replaceStepInUrl("product");
          }
        },

        // ========================================================================
        // VALIDATION ACTIONS
        // ========================================================================

        validateStep: (step) => {
          const state = get();

          switch (step) {
            case "product": {
              const isValid = state.selectedProducts.length > 0;
              return {
                isValid,
                errors: isValid ? [] : ["Please select at least one product"],
                canProceed: isValid,
              };
            }

            case "cadence": {
              // A cadence plan is always preselected (defaults to baseline),
              // so the user can always proceed.
              return { isValid: true, errors: [], canProceed: true };
            }

            case "addons": {
              // Add-ons are optional, always valid
              return { isValid: true, errors: [], canProceed: true };
            }

            case "clinic": {
              const isValid = state.selectedClinic !== null;
              return {
                isValid,
                errors: isValid ? [] : ["Please select a clinic"],
                canProceed: isValid,
              };
            }

            case "date": {
              const errors: string[] = [];
              const firstAvailableDate = state.getFirstAvailableDate();

              if (!state.selectedDate) {
                errors.push("Please select a date");
              } else if (
                isBefore(
                  startOfDay(state.selectedDate),
                  startOfDay(firstAvailableDate),
                )
              ) {
                errors.push("Selected date is before first available date");
              }

              if (state.selectedTimeSlots.length === 0) {
                errors.push("Please select at least one time slot");
              }

              if (state.selectedTimeSlots.length > 2) {
                errors.push("Maximum 2 time slots allowed");
              }

              return {
                isValid: errors.length === 0,
                errors,
                canProceed: errors.length === 0,
              };
            }

            case "details": {
              // Form validation happens during submission
              return { isValid: true, errors: [], canProceed: true };
            }

            default:
              return { isValid: true, errors: [], canProceed: true };
          }
        },

        canProceedFromStep: (step) => {
          return get().validateStep(step).canProceed;
        },

        // ========================================================================
        // TOAST MANAGEMENT
        // ========================================================================

        showToast: (toastMessage) => {
          console.log("[BookingFlow] Showing toast:", toastMessage);

          const toastFn = {
            info: toast.info,
            warning: toast.warning,
            error: toast.error,
            success: toast.success,
          }[toastMessage.type];

          toastFn(toastMessage.message, {
            duration: toastMessage.duration || 5000,
            position: toastMessage.position || "bottom-left",
          });
        },

        // ========================================================================
        // PERSISTED DATE VALIDATION
        // ========================================================================

        _validatePersistedDate: () => {
          let { selectedDate } = get();
          const { wasDateManuallySelected, language, _products } = get();
          console.log(
            "[BookingFlow] Validating persisted date, wasManuallySelected:",
            wasDateManuallySelected,
          );

          if (!selectedDate) return;

          // Defensive: ensure selectedDate is a Date object
          if (!(selectedDate instanceof Date)) {
            console.warn(
              "[BookingFlow] _validatePersistedDate: selectedDate is not a Date, converting:",
              selectedDate,
              "type:",
              typeof selectedDate,
            );
            const parsed = new Date(selectedDate as unknown as string);
            if (isNaN(parsed.getTime())) {
              console.error(
                "[BookingFlow] _validatePersistedDate: Failed to parse selectedDate, clearing:",
                selectedDate,
              );
              set({ selectedDate: null, selectedTimeSlots: [] });
              return;
            }
            selectedDate = parsed;
            set({ selectedDate: parsed });
          }

          const selectedDateStart = startOfDay(selectedDate);
          const today = startOfDay(new Date());

          // First check: is the date in the past?
          const isInPast = isBefore(selectedDateStart, today);

          // Second check: if products are loaded, check against first available date
          let isBeforeFirstAvailable = false;
          if (_products.length > 0) {
            const firstAvailableDate = get().getFirstAvailableDate();
            const firstAvailableStart = startOfDay(firstAvailableDate);
            isBeforeFirstAvailable = isBefore(
              selectedDateStart,
              firstAvailableStart,
            );
          }

          const isInvalid = isInPast || isBeforeFirstAvailable;

          if (isInvalid) {
            console.log(
              "[BookingFlow] Persisted date is no longer valid, clearing selection",
              { isInPast, isBeforeFirstAvailable, wasDateManuallySelected },
            );

            trackEvent({
              event: "persisted_selection_invalidated",
              props: {
                kind: "date_past_or_below_first_available",
                is_in_past: isInPast,
                is_before_first_available: isBeforeFirstAvailable,
                was_manual: wasDateManuallySelected,
                step_when_fired: get().currentStep,
              },
            });

            // Only show toast if user explicitly selected the date (not auto-selected)
            if (wasDateManuallySelected) {
              const locale =
                language === "de-DE"
                  ? de
                  : language === "fr-FR"
                    ? fr
                    : language === "nl-NL"
                      ? nl
                      : enUS;
              const lng = languageMap[language];
              const dateStr = format(selectedDate, "PPP", { locale });

              const message = i18next.t(
                "toast.previouslySelectedDateNoLongerAvailable",
                {
                  lng,
                  date: dateStr,
                },
              );

              get().showToast({
                message,
                type: "warning",
                duration: 6000,
                position: "bottom-left",
              });
            } else {
              console.log(
                "[BookingFlow] Skipping toast - date was auto-selected, not manually chosen",
              );
            }

            const { currentStep } = get();

            set({
              selectedDate: null,
              selectedTimeSlots: [],
            });

            // If user is on details step with invalid date, redirect to date step
            if (currentStep === "details") {
              console.log(
                "[BookingFlow] Redirecting from details to date step due to invalid date",
              );
              const order = getActiveStepOrder();
              set({
                currentStep: "date",
                stepHistory: order.slice(
                  0,
                  order.indexOf("date"),
                ) as BookingStep[],
              });
              replaceStepInUrl("date");
            }
          }
        },

        // ========================================================================
        // RESET ACTIONS
        // ========================================================================

        resetFlow: () => {
          console.log("[BookingFlow] Resetting entire flow");
          set({
            ...initialState,
            _products: get()._products, // Keep product data
            _clinics: get()._clinics, // Keep clinic data
            language: get().language, // Keep language
            market: get().market, // Keep market (country) — survives resets
          });
        },

        resetFromStep: (step) => {
          console.log("[BookingFlow] Resetting from step:", step);

          const resetMap: Record<BookingStep, Partial<BookingFlowState>> = {
            product: {
              selectedProducts: [],
              selectedAddOns: [],
              configureProducts: [],
              selectedPackageId: null,
              selectedClinic: null,
              selectedDate: null,
              selectedTimeSlots: [],
              formData: null,
              currentStep: "product",
              stepHistory: [],
            },
            cadence: {
              selectedAddOns: [],
              selectedClinic: null,
              selectedDate: null,
              selectedTimeSlots: [],
              formData: null,
            },
            addons: {
              selectedAddOns: [],
              selectedClinic: null,
              selectedDate: null,
              selectedTimeSlots: [],
              formData: null,
            },
            clinic: {
              selectedClinic: null,
              selectedDate: null,
              selectedTimeSlots: [],
              formData: null,
            },
            date: {
              selectedDate: null,
              selectedTimeSlots: [],
              formData: null,
            },
            details: {
              formData: null,
            },
            time: {},
            confirmation: {},
          };

          set(resetMap[step] || {});
        },

        // ========================================================================
        // SESSION CONFLICT RESOLUTION
        // ========================================================================

        resolveSessionConflict: (choice: "session" | "url" | "startOver") => {
          const clearConflict = {
            pendingSessionConflict: false,
            _conflictSessionProducts: [] as string[],
            _conflictSessionAddOns: [] as string[],
            _conflictUrlProducts: [] as string[],
          };
          const preserveRuntime = {
            _products: get()._products,
            _clinics: get()._clinics,
            _clinicProductAvailability: get()._clinicProductAvailability,
            _clinicExternalSlots: get()._clinicExternalSlots,
            language: get().language,
          };

          // On the one-off handoff no choice brings back the membership the
          // restored session held: the visit books once, and the syncs below
          // record it dropped.
          get()._dropMembershipOnOneTimeHandoff();

          if (choice === "session") {
            // Keep cart as-is, just close dialog
            console.log(
              "[BookingFlow] Session conflict resolved: keeping session",
            );
            set(clearConflict);
          } else if (choice === "url") {
            // Replace cart with URL products
            console.log(
              "[BookingFlow] Session conflict resolved: using URL products",
            );
            const urlProductIds = get()._conflictUrlProducts;
            const { _products } = get();
            clearSessionIdCookie();

            // Split URL products into packages and add-ons
            const urlPackages = urlProductIds.filter((id) => {
              const p = _products.find((prod) => prod.id === id);
              return !p || p.product_type === "package";
            });
            const urlAddOns = urlProductIds.filter((id) => {
              const p = _products.find((prod) => prod.id === id);
              return p && p.product_type !== "package";
            });
            const urlPackage = _products.find(
              (p) => urlPackages.includes(p.id) && p.product_type === "package",
            );

            // Generate a new session ID immediately so it gets persisted to
            // localStorage before the user can navigate to another URL
            const newSessionId = generateSessionId();
            setSessionIdCookie(newSessionId);
            console.log(
              "[BookingFlow] Session conflict resolved: generated new session ID:",
              newSessionId,
            );

            // A membership entry (?packageType=membership) resumes into the
            // membership flow, which starts at cadence and has no product step.
            // Read the URL rather than carrying a flag through every conflict
            // code path — it's the same source the entry itself used.
            const isMembershipEntry =
              new URLSearchParams(window.location.search).get("packageType") ===
              "membership";
            if (isMembershipEntry) {
              useMembershipPlanStore.getState().setActive(true);
            }
            const targetStep = isMembershipEntry
              ? "cadence"
              : urlPackages.length > 0
                ? (cartEntryStep(!!urlPackage) ?? "addons")
                : "product";
            set({
              ...initialState,
              ...clearConflict,
              sessionId: newSessionId,
              selectedProducts: urlPackages,
              selectedAddOns: urlAddOns,
              selectedPackageId: urlPackage?.id || null,
              selectedPackageType:
                urlPackage?.package_types?.[0] ||
                initialState.selectedPackageType,
              viewMode: urlPackage ? "packages" : "configure",
              // Advance past product step since products are already selected
              currentStep: targetStep,
              hasInitializedFromUrl: true,
              ...preserveRuntime,
            });
            // Update URL to reflect the new step (set() alone doesn't touch the URL)
            pushStepToHistory(targetStep);
            // Sync to server immediately so session has data if user navigates away
            get().syncSession("conflictResolved");
          } else {
            // Start over — clear everything
            console.log(
              "[BookingFlow] Session conflict resolved: starting over",
            );
            // Generate a new session ID immediately so it gets persisted
            const freshSessionId = generateSessionId();
            setSessionIdCookie(freshSessionId);
            set({
              ...initialState,
              ...clearConflict,
              sessionId: freshSessionId,
              currentStep: "product",
              hasInitializedFromUrl: true,
              ...preserveRuntime,
            });
            // Sync to server immediately so session has data if user navigates away
            get().syncSession("conflictResolved");
          }
        },

        clearCart: () => {
          console.log(
            "[BookingFlow] Clearing cart from localStorage and cookies",
          );
          // Clear persisted cart data from localStorage
          try {
            safeLocalStorage.removeItem(CART_STORAGE_KEY);
          } catch (error) {
            console.error(
              "[BookingFlow] Error clearing cart from localStorage:",
              error,
            );
          }
          // NOTE: We do NOT clear sessionId here anymore.
          // The sessionId is cleared by goToStep("confirmation") AFTER syncing
          // the confirmation step to the server. This ensures the full session
          // timeline is recorded before starting a fresh session.
          // The flow will be reset when the user starts a new booking.
        },

        // ========================================================================
        // SELECTORS (Computed State)
        // ========================================================================

        getFirstAvailableDate: () => {
          const {
            selectedClinic,
            selectedProducts,
            selectedAddOns,
            _clinics,
            _products,
          } = get();

          return cachedGetFirstAvailableDate(
            selectedClinic,
            selectedProducts,
            selectedAddOns,
            _clinics,
            _products,
          );
        },

        getDefaultSecondSlotDate: () => {
          const {
            selectedTimeSlots,
            selectedClinic,
            selectedProducts,
            selectedAddOns,
            _clinics,
            _products,
          } = get();

          return cachedGetDefaultSecondSlotDate(
            selectedTimeSlots,
            selectedClinic,
            selectedProducts,
            selectedAddOns,
            _clinics,
            _products,
          );
        },

        isDateValid: () => {
          const { selectedDate } = get();
          if (!selectedDate) return false;

          const firstAvailableDate = get().getFirstAvailableDate();
          return !isBefore(
            startOfDay(selectedDate),
            startOfDay(firstAvailableDate),
          );
        },

        getSelectedProductDetails: () => {
          const { selectedProducts, _products } = get();
          return cachedGetSelectedProductDetails(selectedProducts, _products);
        },

        getSelectedAddOnDetails: () => {
          const { selectedAddOns, _products } = get();
          return cachedGetSelectedAddOnDetails(selectedAddOns, _products);
        },

        getSelectedClinicDetails: () => {
          const { selectedClinic, _clinics } = get();
          return cachedGetSelectedClinicDetails(selectedClinic, _clinics);
        },

        getInferredPackageType: () => {
          const { selectedProducts, _products, selectedPackageType } = get();

          if (selectedProducts.length === 0) {
            return selectedPackageType;
          }

          // Find the first selected product and check its package type
          const selectedProduct = _products.find((p) =>
            selectedProducts.includes(p.id),
          );
          if (
            selectedProduct?.package_types &&
            selectedProduct.package_types.length > 0
          ) {
            return selectedProduct.package_types[0];
          }

          return selectedPackageType;
        },

        getAvailablePackageTypes: () => {
          const { _products } = get();

          const types = new Set<string>();
          const packageProducts = _products.filter(
            (p) => p.product_type === "package" && p.status === "active",
          );

          // Collect all package types from active packages
          packageProducts.forEach((product) => {
            if (product.package_types) {
              product.package_types.forEach((type) => {
                types.add(type);
              });
            }
          });

          // Return all available types with MRI first, then blood-only
          const sorted = Array.from(types).sort((a, b) => {
            if (a === "mri") return -1;
            if (b === "mri") return 1;
            return a.localeCompare(b);
          });
          return sorted;
        },

        getMissingRequirements: (step) => {
          const state = get();
          const { language } = state;
          const requirements = BOOKING_STEP_REQUIREMENTS[step] || [];
          return (
            requirements
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .filter((req) => !req.validate((state as any)[req.field]))
              .map((req) => {
                const lng = languageMap[language];
                return i18next.t(req.errorMessage, { lng });
              })
          );
        },

        ensureStepValidity: (step) => {
          console.log(
            "[BookingFlow] ensureStepValidity called for step:",
            step,
          );
          // Walk the active path (membership cadence vs standard add-ons) so the
          // membership-only `cadence` step is recognised instead of bouncing to
          // product (indexOf -1) when membership mode is on.
          const stepOrder = getActiveStepOrder();
          const stepIndex = stepOrder.indexOf(step);
          console.log(
            "[BookingFlow] stepIndex:",
            stepIndex,
            "stepOrder:",
            stepOrder,
          );
          if (stepIndex <= 0) return stepOrder[0];

          // Check all previous steps (including the target step itself)
          for (let i = 0; i <= stepIndex; i++) {
            const s = stepOrder[i];
            const missing = get().getMissingRequirements(s);
            console.log(
              `[BookingFlow] Step ${s} missing requirements:`,
              missing,
            );
            if (missing.length > 0) {
              console.log(
                `[BookingFlow] Step ${s} is missing requirements:`,
                missing,
              );
              return s; // The first invalid step
            }
          }
          console.log("[BookingFlow] ensureStepValidity returning:", step);
          return step;
        },

        // ========================================================================
        // INTERNAL HELPERS (prefixed with _)
        // ========================================================================

        // After a `?products=` cart is applied on its own: the fresh path, and
        // a returning visitor's empty cart (loadSession, its deferred apply in
        // setProducts). The returning visitor's link opens where a fresh one's
        // does; an empty restored cart left the quiz's and the one-off
        // handoff's visitors on the product step (Eugene, 22.10).
        _openCartEntryStep: (hasPackage) => {
          const entryStep = cartEntryStep(hasPackage);
          if (!entryStep || get().ensureStepValidity(entryStep) !== entryStep) {
            return;
          }
          const stepOrder = getActiveStepOrder();
          set({
            currentStep: entryStep,
            stepHistory: stepOrder.slice(0, stepOrder.indexOf(entryStep)),
          });
          replaceStepInUrl(entryStep);
        },

        // A "once" pick on the membership-first tests screen is a one-off
        // booking (Eugene, 22.10). The membership, bundle and membership-first
        // funnel a restored session holds would sell it as one, and the
        // checkout marker would count it as a membership signup.
        _dropMembershipOnOneTimeHandoff: () => {
          if (!isOneTimeHandoff()) return;
          const planStore = useMembershipPlanStore.getState();
          planStore.setActive(false);
          planStore.setMembershipFirst(false);
          planStore.setBundleAttached(false);
          set({ membershipCheckoutInitiated: null });
        },

        _recalculateAndValidateDate: () => {
          let { selectedDate } = get();
          const { language } = get();
          console.log("[BookingFlow] Recalculating and validating date");

          // Defensive: ensure selectedDate is a Date object
          if (selectedDate && !(selectedDate instanceof Date)) {
            console.warn(
              "[BookingFlow] _recalculateAndValidateDate: selectedDate is not a Date, converting:",
              selectedDate,
              "type:",
              typeof selectedDate,
            );
            const parsed = new Date(selectedDate as unknown as string);
            if (isNaN(parsed.getTime())) {
              console.error(
                "[BookingFlow] _recalculateAndValidateDate: Failed to parse selectedDate, clearing:",
                selectedDate,
              );
              set({ selectedDate: null, selectedTimeSlots: [] });
              return;
            }
            selectedDate = parsed;
            set({ selectedDate: parsed });
          }

          const newFirstAvailableDate = get().getFirstAvailableDate();
          if (selectedDate) {
            const currentDateStart = startOfDay(selectedDate);
            const newFirstAvailableStart = startOfDay(newFirstAvailableDate);
            const isInvalid = isBefore(
              currentDateStart,
              newFirstAvailableStart,
            );

            if (isInvalid) {
              console.log(
                "[BookingFlow] Current date became invalid, showing toast and auto-selecting new date",
              );

              trackEvent({
                event: "persisted_selection_invalidated",
                props: {
                  kind: "date_below_first_available_after_product_change",
                  was_manual: get().wasDateManuallySelected,
                  step_when_fired: get().currentStep,
                },
              });

              const locale =
                language === "de-DE"
                  ? de
                  : language === "fr-FR"
                    ? fr
                    : language === "nl-NL"
                      ? nl
                      : enUS;
              const lng = languageMap[language];
              const dateStr = format(selectedDate, "PPP", { locale });
              const newDateStr = format(newFirstAvailableDate, "PPP", {
                locale,
              });

              const message = i18next.t("toast.dateNoLongerAvailable", {
                lng,
                date: dateStr,
                newDate: newDateStr,
              });

              get().showToast({
                message,
                type: "warning",
                duration: 5000,
                position: "bottom-left",
              });

              set({
                selectedTimeSlots: [],
                selectedDate: newFirstAvailableDate,
              });
            }
          }
          get()._validateTimeSlotsForClinic();
        },

        _validateTimeSlotsForClinic: () => {
          if (get().pickedTimesPaid) return;
          const {
            selectedTimeSlots,
            selectedClinic,
            _clinics,
            _clinicExternalSlots,
            language,
          } = get();

          if (selectedTimeSlots.length === 0 || !selectedClinic) {
            return;
          }

          // Don't validate until clinics are loaded - we need clinic data for opening_hours fallback
          if (_clinics.length === 0) {
            console.log(
              "[BookingFlow] _validateTimeSlotsForClinic: Skipping - clinics not loaded yet",
            );
            return;
          }

          // Defensive: ensure all dates in selectedTimeSlots are Date objects
          // This handles cases where dates might be strings from incomplete deserialization
          const normalizedSlots: TimeSlot[] = [];
          for (const slot of selectedTimeSlots) {
            let slotDate = slot.date;
            if (!(slotDate instanceof Date)) {
              console.warn(
                "[BookingFlow] _validateTimeSlotsForClinic: slot.date is not a Date, converting:",
                slotDate,
                "type:",
                typeof slotDate,
              );
              slotDate = new Date(slotDate as unknown as string);
              if (isNaN(slotDate.getTime())) {
                console.error(
                  "[BookingFlow] _validateTimeSlotsForClinic: Failed to parse slot date, skipping:",
                  slot.date,
                );
                continue;
              }
            }
            normalizedSlots.push({ ...slot, date: slotDate });
          }

          // If we had to normalize any dates, update the store
          if (
            normalizedSlots.length !== selectedTimeSlots.length ||
            normalizedSlots.some(
              (s, i) => s.date !== selectedTimeSlots[i]?.date,
            )
          ) {
            console.log(
              "[BookingFlow] _validateTimeSlotsForClinic: Updating store with normalized dates",
            );
            set({ selectedTimeSlots: normalizedSlots });
          }

          const clinic = _clinics.find((c) => c.id === selectedClinic);
          const externalSlots = _clinicExternalSlots[selectedClinic];
          const bookingMode = get().bookingMode;

          // Validate against the EXACT same set of times the picker offers
          // (TimeSlotGrid), via the shared helper — so we can never delete a
          // slot the picker just showed. This covers the sparse-clinic case
          // where an external entry with empty times still combines down to the
          // opening-hours request slots (AHD external-slot bug).
          const getAvailableTimesForDate = (date: Date): string[] =>
            getBookableTimesForDate(clinic, externalSlots, date, bookingMode);

          const validSlots: TimeSlot[] = [];
          const invalidSlots: TimeSlot[] = [];

          for (const slot of normalizedSlots) {
            const availableTimes = getAvailableTimesForDate(slot.date);
            if (availableTimes.includes(slot.time)) {
              validSlots.push(slot);
            } else {
              invalidSlots.push(slot);
            }
          }

          if (invalidSlots.length > 0) {
            const locale =
              language === "de-DE"
                ? de
                : language === "fr-FR"
                  ? fr
                  : language === "nl-NL"
                    ? nl
                    : enUS;
            const lng = languageMap[language];
            const invalidTimesStr = invalidSlots
              .map(
                (slot) => `${format(slot.date, "PP", { locale })} ${slot.time}`,
              )
              .join(", ");

            const message = i18next.t("toast.timeSlotsNoLongerAvailable", {
              lng,
              times: invalidTimesStr,
              count: invalidSlots.length,
            });

            console.log("[test] timeSlotsNoLongerAvailable", {
              invalidCount: invalidSlots.length,
              remainingCount: validSlots.length,
            });

            trackEvent({
              event: "persisted_selection_invalidated",
              props: {
                kind: "time_slot_clinic_mismatch",
                invalid_count: invalidSlots.length,
                remaining_count: validSlots.length,
                was_manual: get().wasDateManuallySelected,
                step_when_fired: get().currentStep,
              },
            });

            get().showToast({
              message,
              type: "warning",
              duration: 6000,
              position: "bottom-left",
            });

            set({ selectedTimeSlots: validSlots });

            if (validSlots.length === 0 && get().currentStep === "details") {
              get().goToStep("date", { skipValidation: true });
            }
          }
        },
      }),
      {
        name: CART_STORAGE_KEY,
        storage: createJSONStorage(() => safeLocalStorage),
        partialize: (state): PersistedCartState => ({
          sessionId: state.sessionId,
          language: state.language,
          market: state.market,
        }),
        onRehydrateStorage: () => (state) => {
          // Market resolution on rehydrate, in priority order. Progress lives
          // server-side (keyed by sessionId), so switching market means dropping
          // the sessionId — that both stops loadSession below from restoring the
          // OTHER market's flow and cancels its progress.
          const urlMarket = parseMarketFromUrl(
            new URLSearchParams(window.location.search),
          );
          if (state && urlMarket && urlMarket !== state.market) {
            // An explicit ?country= deep link is authoritative: switch market and
            // cancel the other market's in-progress booking. (This is the fix for
            // ?country=de landing on Switzerland when a Swiss session existed.)
            console.log(
              "[BookingFlow] ?country override on rehydrate -> ",
              urlMarket,
            );
            state.market = urlMarket;
            state.sessionId = null;
            clearSessionIdCookie();
          } else if (
            state &&
            state.market !== "ch" &&
            urlMarket !== state.market
          ) {
            // TEMP(AHD-7105): DE/NL aren't live for general traffic, so a stale
            // persisted non-CH market (no matching ?country= in the URL) must not
            // strand a returning visitor in a URL-only market — coerce back to CH
            // and drop the session. REMOVE when those markets launch for all.
            console.log(
              `[BookingFlow] TEMP: coercing persisted market ${state.market} -> ch`,
            );
            state.market = "ch";
            state.sessionId = null;
            clearSessionIdCookie();
          }

          // Don't load session if URL has step=confirmation - this means user
          // refreshed/landed on confirmation page, which should reset the flow
          const urlStep = new URLSearchParams(window.location.search).get(
            "step",
          );
          if (urlStep === "confirmation") {
            console.log(
              "[BookingFlow] Skipping session load - URL has step=confirmation, will reset flow",
            );
            // Clear the sessionId so a fresh one is generated
            if (state) {
              state.sessionId = null;
            }
            clearSessionIdCookie();
            return;
          }

          if (state?.sessionId) {
            console.log(
              "[BookingFlow] Rehydrated sessionId, fetching state from server:",
              state.sessionId,
            );
            state.loadSession(state.sessionId);
          }
        },
      },
    ),
    {
      name: "booking-flow-store",
      enabled: true,
    },
  ),
);

// English copy is per market (en-NL, en-DE): keep i18next's "en" filled with
// the active market's file, including after a rehydrated session restores it.
applyMarketEnglish(useBookingFlowStore.getState().market);
useBookingFlowStore.subscribe((state, prev) => {
  if (state.market !== prev.market) applyMarketEnglish(state.market);
});

// ============================================================================
// EXTERNAL SELECTORS (for use with useBookingFlowStore)
// ============================================================================

export const selectCanProceed = (state: BookingFlowState) =>
  state.canProceedFromStep(state.currentStep);

export const selectCurrentStepValidation = (state: BookingFlowState) =>
  state.validateStep(state.currentStep);

// ============================================================================
// TAB FOCUS LISTENER - Sync state from server when tab regains focus
// ============================================================================

refreshOnTabFocus(() => useBookingFlowStore.getState().refreshFromServer());
